import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
} from 'react';
import { getStoredLoginIdentifier, getStoredLoginType, getSessionToken } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import {
  ASSET_CREATE_MAX_IMAGE_FILE_BYTES,
  ASSET_CREATE_UI_MAX_IMAGES,
  ASSET_PATCH_MULTIPART_JSON_MINIMAL,
} from '../constants/assetCreateUpload';
import {
  filterPricingTypesForAssetCreate,
  stripDisallowedPricingMethodsFromAssetBody,
} from '../constants/assetCreatePricingTypes';
import {
  browseFilterAssetTypes,
  browseFilterLabelForTypeId,
  buildHomeCarouselTypeIdSets,
  buildOfficeVoiceSearchTypeIdSet,
  isHomeGymBrowseTypeSelection,
  isPrivateOfficeBrowseTypeSelection,
  matchBrowseFilterTypeId,
} from '../constants/filterableAssetTypes';
import { assetMatchesHomeGymCarousel, assetMatchesOfficeVoiceSearch } from '../utils/homeCarouselAssetMatch';
import {
  canBookAssets,
  canBrowseBookableAssets,
  canMutateAssets as canMutateAssetsForLoginType,
  canUseAssetsMenu,
  isBusinessClientLogin,
} from '../auth/userLoginType';
import { useBookingCreate, type QuickBookingDraft } from '../booking/BookingCreateContext';
import { useProfileBookingGate } from '../booking/ProfileBookingGateContext';
import { prepareFavoriteAssetSnapshot } from '../favorites/favoriteAssetsStorage';
import {
  invalidateAssetThumbnailCacheForId,
  registerAssetThumbnailRecords,
  registerAssetVenueRecord,
} from '../utils/assetThumbnailStore';
import { invalidateBookingAssetThumbnailSearchCache } from '../utils/bookingAssetThumbnailCache';
import { recordRecentlyViewedAsset } from '../utils/recentlyViewedAssetsStorage';
import {
  clearPersistedAssetCreateAttempt,
  loadPersistedAssetCreateAttempt,
  persistAssetCreateAttempt,
  stripOpeningTimesDateOverridesFromAssetBody,
} from '../utils/assetCreateAttemptStorage';
import {
  clearPersistedAssetCreateImagePreviews,
  loadPersistedAssetCreateImagePreviews,
  persistAssetCreateImagePreviews,
  reconcileDraftCreateImages,
} from '../utils/assetCreateImageDraftStorage';
import {
  applyAssetCreateSubmitDefaults,
  applyAssetMutationSubmitDefaults,
  assetCreateVisibleFormData,
  assetEditVisibleFormData,
} from '../utils/assetCreateFormDefaults';
import { ISRAEL_COUNTRY_ID } from '../utils/assetCountryCurrency';
import { partitionAssetCreateFacilityCatalog } from '../utils/assetCreateFacilities';
import {
  buildDefaultAssetCreateOpeningTimes,
  isAssetCreateOpeningTimesUnset,
} from '../utils/openingTimesLocale';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useUserLocationContext } from '../context/UserLocationContext';
import pageThemeStyles from './menuPageTheme.module.css';
import {
  buildAssetPatchDelta,
  coerceAssetBodyOpeningTimes,
  estimateAssetPatchBodyBytes,
  ensureAddressEditorShape,
  isAssetUpdateEditorReadonlyWirePath,
  normalizeAssetImagesToWireStrings,
  prepareAssetRequestBody,
  sanitizeAssetPatchRequestBody,
} from '../api/assetPayloadNormalize';
import {
  emptyAssetCreateBody,
  emptyAssetUpdateBody,
} from '../api/assetDefaultBodies';
import {
  loadGeneralInfoCountries,
  loadGeneralInfoRegions,
  type GeneralInfoPicklistItem,
  type GeneralInfoPicklistsProp,
} from '../api/generalInfoApi';
import {
  fetchActiveAssetsListing,
  fetchMyAssetsListing,
  fetchSearchAssetsForUiState,
} from '../api/assetListingApi';
import { hasClientSideBrowseFilters, ASSET_SEARCH_KEYWORD_DEBOUNCE_MS, fetchActiveAssetCatalogStaged } from '../api/assetSearchClient';
import {
  applySuggestionToPricingSelection,
  resolveAssetCreatePriceSuggestion,
  suggestedPriceForPricingTypeId,
  type AssetCreatePriceSuggestion,
} from '../utils/assetCreatePricingSuggestions';
import {
  AssetRequestError,
  buildAssetCreateMultipartFormData,
  createAsset,
  createAssetWithMultipartFormData,
  extractAssetIdentifierFromApiPayload,
  updateAssetWithMultipartFormData,
  fetchAssetFacilities,
  fetchAssetPricingTypes,
  fetchAssetTypes,
  fetchAssetTypesForBrowse,
  getAssetById,
  getAssetByIdBestEffort,
  requestAssetApi,
  shortAssetRequestErrorSummary,
} from '../api/getAssetById';
import { AssetShortIdNotFoundError } from '../api/resolveAssetByShortDisplayId';
import { fetchProfile } from '../api/profileApi';
import { buildAuthorizedHeaders } from '../api/withApiAuth';
import { AssetCreateFacilityCheckboxGrid } from './AssetCreateFacilityCheckboxGrid';
import { AssetCreateTermsSection } from './AssetCreateTermsSection';
import { AssetBookingAvailabilitySection } from './AssetBookingAvailabilitySection';
import {
  AssetGetByIdView,
  assetRecordDisplayTitle,
  assetRecordPrimaryId,
  assetRecordResolvedImageSrcs,
  canRenderAssetGetByIdView,
} from './AssetGetByIdView';
import { isAssetKeywordQueryActive, refineAssetKeywordSearchResults } from '../utils/assetKeywordSearch';
import {
  resolveAssetForBookingCreate,
  assetRecordIsBookable,
  persistAssetBookingAvailability,
  AssetBookingAvailabilityPersistError,
} from '../utils/assetBookability';
import { assetRecordMutationId } from '../utils/assetRecordPrimaryId';
import { scheduleScrollPanelToElement } from '../utils/panelFocusScroll';
import { mergeAssetApiRecordWithListSnapshot } from '../utils/assetRecordMerge';
import {
  applyUploadedImagePreviewsToAssetRecord,
  buildAssetUpdateDisplayImages,
} from '../utils/assetUpdateImageDisplay';
import {
  browseAssetListSnapshotForId,
  rememberBrowseAssetListSnapshot,
} from '../utils/browseAssetListSnapshot';
import {
  assetRecordMatchesBrowseCity,
  assetRecordMatchesBrowseStreet,
  filterAssetRecordsForUiState,
  preferRecordsMatchingAssetIds,
} from '../utils/assetSearchRecordFilters';
import { assetBrowseShowsRegionFilter } from '../utils/assetBrowseFilters';
import {
  sortAssetBrowseListRecords,
  type AssetBrowseListSortMode,
} from '../utils/assetBrowseListSort';
import { useBookableAssetSearch } from '../hooks/useBookableAssetSearch';
import { assetSearchUiStateFromBrowseFilters } from '../utils/assetSearchUiState';
import { refineAssetsForOpen24hSearch, facilityCatalog24x7Ids } from '../utils/assetOpen24hMatch';
import { refineAssetsForAmenityIcons } from '../utils/assetAmenityMatch';
import type { FacilityIconId } from '../utils/assetFacilityIcons';
import { BookableAssetSearchResults } from './BookableAssetSearchResults';
import { PickedFileImageThumb } from './AssetImageThumb';
import { AssetListView } from './AssetListView';
import { AssetQuickBookingPanel } from './AssetQuickBookingPanel';
import { AssistantSearchSlotChip } from '../assistant/AssistantSearchSlotChip';
import { assistantSearchChipFromBrowse } from '../assistant/formatAssistantSearchChip';
import {
  isAssistantOpenWeekdayFilter,
  refineAssetsForAssistantSchedule,
} from '../assistant/assistantSchedule';
import { floorAssistantTimeToHour } from '../assistant/parseAssistantWhen';
import {
  commitAssistantSearchHits,
  getAssistantSearchHits,
  isAssistantSearchHitsReady,
} from '../assistant/assistantSearchHits';
import { AssetOwnerSchedulePanel } from './AssetOwnerSchedulePanel';
import { NonNegativePriceInput } from './NonNegativePriceInput';
import { DynamicRecordForm } from './DynamicRecordForm';
import { AssetRequestErrorPanel } from './AssetRequestErrorPanel';
import { deepClone, setAtPath } from '../utils/cloneAndPath';
import { prepareImageFilesForMultipartUpload } from '../utils/compressImageFilesForAssetUpload';
import { isExplicitlyEmptyAssetList, normalizeAssetListResponse } from '../utils/assetListNormalize';
import { migrateAssetBodyPromotedAddonsToFacilities } from '../utils/assetPromotedFacilities';
import {
  applyCoordinatesToAssetBody,
  buildAddressGeocodeQuery,
  parseAssetCoordinates,
  type AddressGeocodeLabels,
} from '../utils/assetGeo';
import { applyPostalCodeToAssetBody, enrichAssetRecordAddressForDisplay, enrichAssetRecordAddressForDisplayWithGeocode } from '../utils/assetAddressPostalCode';
import { applyAssetCreateContactDefaults } from '../utils/assetCreateOwnerDefaults';
import {
  assetCreateMandatoryMarkerKeys,
  assetUpdateMandatoryMarkerKeys,
  assertAssetCreateBodyReady,
  assertAssetUpdateBodyReady,
  assetBodyHasInvalidPricingPrices,
} from '../utils/assetCreateValidation';
import { geocodeAssetAddressDetails } from '../utils/resolveIsraelAddressGeo';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import { resolveAssetBodyGeo, resolveAssetBodyGeoForUpdate } from '../utils/resolveAssetBodyGeo';
import { assetRecordMatchesDisplayId, assetRecordShortDisplayId, formatAssetShortId, isAssetShortDisplayId } from '../utils/assetShortId';
import { canMutateAssetAsSessionOwner, extractAssetOwnerId } from '../utils/assetOwnerMatch';
import { isLoadableImageWirePath } from '../utils/assetImageResolve';
import { imageFilesToPreviewDataUrls } from '../utils/assetImagesWire';
import {
  CreateAssetImageRepickRequiredError,
  planCreateAssetImageUpload,
  sanitizeCreateEditorImagePaths,
} from '../utils/assetCreateImageSubmit';
import {
  labelWithCurrencySymbol,
  localizeAssetPriceFieldLabel,
  localizeAssetTypeLabel,
  localizeCatalogRowTitle,
  localizeGeoLabel,
  resolveAssetLocaleCurrency,
  useI18n,
} from '../i18n';
import { assetOperationSelectLabel, assetOperations } from '../i18n/operationLabels';
import type { TranslateFn } from '../i18n/operationLabels';
import type { AppLanguage } from '../i18n/types';
import styles from './AssetsMenu.module.css';

type Operation = 'getById' | 'listMine' | 'active' | 'create';

/** Successful panel responses may include PATCH/DELETE from the unified Asset flow (not selectable as operations). */
type AssetPanelResponseOperation = Operation | 'update' | 'delete';

function normalizeAssetOpId(raw: string): string {
  return raw.trim().toLowerCase();
}

const DEMO_ASSET_ID = 'e58aa007-9437-4ee2-8f0d-075b7bebff7e';

/** Single persisted id for the unified display / edit / delete flow (last asset you successfully used here). */
const ASSET_LAST_WORKED_ID_STORAGE_KEY = 'ofrontend.assets.lastWorkedAssetId';
const ASSET_UPDATE_LAST_ID_STORAGE_KEY = 'ofrontend.assets.updateLastAssetId';
const ASSET_DISPLAY_LAST_ID_STORAGE_KEY = 'ofrontend.assets.displayLastAssetId';

function persistLastWorkedAssetId(idTrimmed: string): void {
  const t = idTrimmed.trim();
  if (!t || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(ASSET_LAST_WORKED_ID_STORAGE_KEY, t);
    localStorage.setItem(ASSET_DISPLAY_LAST_ID_STORAGE_KEY, t);
    localStorage.setItem(ASSET_UPDATE_LAST_ID_STORAGE_KEY, t);
  } catch {
    /* quota / private mode */
  }
}

function clearLastWorkedAssetIdStorage(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(ASSET_LAST_WORKED_ID_STORAGE_KEY);
    localStorage.removeItem(ASSET_DISPLAY_LAST_ID_STORAGE_KEY);
    localStorage.removeItem(ASSET_UPDATE_LAST_ID_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readLastWorkedAssetId(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const unified = localStorage.getItem(ASSET_LAST_WORKED_ID_STORAGE_KEY)?.trim();
    if (unified) {
      return unified;
    }
    const upd = localStorage.getItem(ASSET_UPDATE_LAST_ID_STORAGE_KEY)?.trim();
    if (upd) {
      return upd;
    }
    const disp = localStorage.getItem(ASSET_DISPLAY_LAST_ID_STORAGE_KEY)?.trim();
    return disp || null;
  } catch {
    return null;
  }
}

/** When the deleted row matches persisted defaults, clear storage so reopening doesn’t revive a removed id. */
function clearLastWorkedAssetIdIfStoredIs(idNormalized: string): void {
  const stored = readLastWorkedAssetId();
  if (!stored || normalizeAssetOpId(stored) !== idNormalized) {
    return;
  }
  clearLastWorkedAssetIdStorage();
}

/** Label for image picker rows — avoid showing server paths, hashes, or long URLs as primary text. */
function assetImagePlaceholderLabel(zeroBasedIndex: number): string {
  return `Image ${zeroBasedIndex + 1}`;
}

const CREATE_ASSET_IMAGES_MAX = ASSET_CREATE_UI_MAX_IMAGES;
const CREATE_ASSET_IMAGE_MAX_MB = ASSET_CREATE_MAX_IMAGE_FILE_BYTES / (1024 * 1024);

/** Normalizes `assetBody.images` to at most {@link CREATE_ASSET_IMAGES_MAX} non-empty path strings. */
function normalizedCreateImagesPaths(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: string[] = [];
  for (const x of value) {
    if (typeof x === 'string') {
      const t = x.trim();
      if (t) {
        out.push(t);
      }
      if (out.length >= CREATE_ASSET_IMAGES_MAX) {
        break;
      }
    }
  }
  return out;
}

/**
 * String sent as one `images[]` entry after file pick. Electron attaches `path`; typical browsers expose only `name`.
 */
function imagePathSubmittedForCreate(file: File): string | null {
  const ext = file as File & { path?: string };
  if (typeof ext.path === 'string' && ext.path.trim()) {
    return ext.path.trim().replace(/\\/g, '/');
  }
  const base = file.name?.trim();
  return base || null;
}

/** Keeps picked {@link File} previews aligned with parallel `images[]` path strings. */
function alignParallelImageFiles(
  files: (File | undefined)[],
  pathCount: number,
): (File | undefined)[] {
  if (files.length === pathCount) {
    return files;
  }
  if (files.length > pathCount) {
    return files.slice(0, pathCount);
  }
  return [...files, ...Array.from({ length: pathCount - files.length }, () => undefined)];
}

function mergeCreateImagePicks(
  existingPaths: string[],
  existingFiles: (File | undefined)[],
  picked: File[],
): { paths: string[]; files: (File | undefined)[] } {
  const paths = [...existingPaths];
  let files = alignParallelImageFiles(existingFiles, existingPaths.length);

  for (const file of picked) {
    if (paths.length >= CREATE_ASSET_IMAGES_MAX) {
      break;
    }
    const path = imagePathSubmittedForCreate(file);
    if (!path) {
      continue;
    }
    const existingIndex = paths.indexOf(path);
    if (existingIndex >= 0) {
      files = files.slice();
      files[existingIndex] = file;
      continue;
    }
    paths.push(path);
    files = [...files, file];
  }

  return { paths, files: files.slice(0, paths.length) };
}

function mergeCreateMainImagePick(
  existingPaths: string[],
  existingFiles: (File | undefined)[],
  mainFile: File,
  mainPath: string,
): { paths: string[]; files: (File | undefined)[] } {
  const curFiles = alignParallelImageFiles(existingFiles, existingPaths.length);

  const additionalPaths: string[] = [];
  const additionalFiles: (File | undefined)[] = [];
  for (let i = 1; i < existingPaths.length; i++) {
    const p = existingPaths[i];
    if (p === mainPath) {
      continue;
    }
    const f = curFiles[i];
    if (f) {
      additionalPaths.push(p);
      additionalFiles.push(f);
    } else if (isLoadableImageWirePath(p)) {
      additionalPaths.push(p);
      additionalFiles.push(undefined);
    }
  }

  const paths = [mainPath, ...additionalPaths].slice(0, CREATE_ASSET_IMAGES_MAX);
  const files = [mainFile, ...additionalFiles].slice(0, paths.length);
  return { paths, files };
}

function operationNeedsAssetId(op: Operation): boolean {
  return op === 'getById';
}

/** Structured create/edit field panel (name, address, facilities, pricing, etc.). */
function showAssetMutationJsonFields(op: Operation, assetEditingWithDisplay: boolean): boolean {
  return op === 'create' || assetEditingWithDisplay;
}

function submitLabel(
  op: Operation,
  loading: boolean,
  updateEditReady: boolean,
  showCloseInsteadOfDisplay: boolean,
  t: TranslateFn,
): string {
  if (loading) {
    return '…';
  }
  switch (op) {
    case 'getById':
      if (updateEditReady) {
        return t('common.update');
      }
      return showCloseInsteadOfDisplay ? t('common.close') : t('common.display');
    case 'listMine':
    case 'active':
      return t('common.fetch');
    case 'create':
      return t('common.create');
    default: {
      const _ex: never = op;
      return _ex;
    }
  }
}

function emptyHintText(op: Operation, canMutateAssets: boolean, t: TranslateFn): string {
  switch (op) {
    case 'getById':
      return '';
    case 'listMine':
      return '';
    case 'active':
      return '';
    case 'create':
      if (!canMutateAssets) {
        return t('assets.createNotForClients');
      }
      return '';
    default: {
      const _n: never = op;
      return String(_n);
    }
  }
}

function isAssetMutationOperation(value: Operation): value is 'create' {
  return value === 'create';
}

function isListLikeOperation(op: Operation): boolean {
  return op === 'listMine' || op === 'active';
}

/** Business/private clients browse bookable assets — no create, list-mine, or mutations. */
function isClientBrowseOperation(op: Operation): boolean {
  return op === 'active' || op === 'getById';
}

function selectableOperations(
  canMutateAssets: boolean,
  operations: ReturnType<typeof assetOperations>,
): ReturnType<typeof assetOperations> {
  if (canMutateAssets) {
    return operations;
  }
  return operations.filter((op) => isClientBrowseOperation(op.value));
}

function isListLikePanelOperation(
  op: AssetPanelResponseOperation,
): op is 'listMine' | 'active' {
  return op === 'listMine' || op === 'active';
}

/** Client browse loads via search hook (`response` stays null); owners use list GET `response`. */
function shouldShowListDetailPanel(
  listDetailAsset: Record<string, unknown> | null,
  clientBrowseActive: boolean,
  response: { operation: AssetPanelResponseOperation; data: unknown } | null,
  deepLinkAssetId?: string | null,
): listDetailAsset is Record<string, unknown> {
  if (!listDetailAsset) {
    return false;
  }
  const deepLink = deepLinkAssetId?.trim();
  if (deepLink) {
    const detailId = assetRecordPrimaryId(listDetailAsset);
    if (detailId && normalizeAssetOpId(detailId) === normalizeAssetOpId(deepLink)) {
      return true;
    }
  }
  if (clientBrowseActive) {
    return true;
  }
  return Boolean(response && isListLikePanelOperation(response.operation));
}

function coerceNumericField(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** ID used for DELETE `/assets/:id`. */
function assetRecordApiId(asset: Record<string, unknown>): string | null {
  const raw = asset.id ?? asset.asset_id;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/** Compare list-row ids case-insistently (UUID case differs across payloads). */
function canonicalListAssetRowId(asset: Record<string, unknown>): string | null {
  const id = assetRecordPrimaryId(asset);
  return id ? id.toLowerCase() : null;
}

function patchAssetStatusInListPayload(
  data: unknown,
  assetIdKey: string,
  status: string,
): unknown {
  const patchRow = (row: Record<string, unknown>): Record<string, unknown> => {
    const rid = canonicalListAssetRowId(row);
    return rid === assetIdKey ? { ...row, asset_status: status } : row;
  };
  if (Array.isArray(data)) {
    return data.map((item) => (isPlainObject(item) ? patchRow(item) : item));
  }
  if (isPlainObject(data)) {
    const next = { ...data };
    for (const key of ['assets', 'data', 'items', 'results', 'rows'] as const) {
      const nested = next[key];
      if (Array.isArray(nested)) {
        next[key] = nested.map((item) => (isPlainObject(item) ? patchRow(item) : item));
      }
    }
    return next;
  }
  return data;
}

/**
 * After a list GET, remove omission entries for ids that no longer appear in the payload.
 * If an id is still returned (stale list), keep omitting it so the row stays hidden.
 */
function pruneDeletedListAssetIdsForPayload(prev: Set<string>, listData: unknown): Set<string> {
  if (prev.size === 0) {
    return prev;
  }
  const list = normalizeAssetListResponse(listData);
  const idsInResponse = new Set<string>();
  for (const a of list) {
    const id = canonicalListAssetRowId(a);
    if (id) {
      idsInResponse.add(id);
    }
  }
  const next = new Set(prev);
  for (const id of Array.from(prev)) {
    if (!idsInResponse.has(id)) {
      next.delete(id);
    }
  }
  return next;
}

/**
 * DELETE `/assets/:id`. HTTP 404 means the asset is already gone server-side (e.g. stale list still
 * shows the row). Caller should still drop the id from the UI.
 */
async function deleteAssetByIdAllowNotFound(id: string): Promise<unknown> {
  const trimmed = id.trim();
  try {
    return await requestAssetApi({ method: 'DELETE', path: `/assets/${encodeURIComponent(trimmed)}` });
  } catch (e) {
    if (e instanceof AssetRequestError && e.httpStatus === 404) {
      return null;
    }
    throw e;
  }
}

/**
 * List GET used by Fetch and post-delete refresh.
 */
async function fetchAssetListing(
  mode: 'listMine' | 'active',
  forClientBrowse: boolean,
): Promise<unknown> {
  if (mode === 'listMine') {
    return fetchMyAssetsListing();
  }
  return fetchActiveAssetsListing(forClientBrowse);
}

/** List rows may carry `country_id` on the root, under `address`, or on nested objects. */
function assetRecordListCountryId(asset: Record<string, unknown>): number | null {
  const from = (o: Record<string, unknown>): number | null => {
    for (const k of ['country_id', 'CountryId', 'countryId'] as const) {
      const n = coerceNumericField(o[k]);
      if (n !== null) {
        return n;
      }
    }
    return null;
  };

  const root = from(asset);
  if (root !== null) {
    return root;
  }

  const addr = asset.address;
  if (isPlainObject(addr)) {
    const n = from(addr);
    if (n !== null) {
      return n;
    }
  }

  for (const nestedKey of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nestedKey];
    if (isPlainObject(nested)) {
      const n = from(nested);
      if (n !== null) {
        return n;
      }
    }
  }

  const countryObj = asset.country;
  if (isPlainObject(countryObj)) {
    return (
      coerceNumericField(countryObj.id) ??
      coerceNumericField(countryObj.country_id) ??
      from(countryObj)
    );
  }

  return null;
}

/** When the list API omits numeric `country_id` but includes a name (e.g. "Israel"). */
function normalizedCountryNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.country_name);
  add(asset.countryName);
  if (typeof asset.country === 'string') {
    add(asset.country);
  }
  if (isPlainObject(asset.country)) {
    const c = asset.country;
    add(c.name);
    add(c.label);
    add(c.country_name);
    add(c.title);
  }
  const addr = asset.address;
  if (isPlainObject(addr)) {
    add(addr.country);
    add(addr.country_name);
    add(addr.countryName);
  }
  for (const nk of ['facility', 'Facility'] as const) {
    const f = asset[nk];
    if (isPlainObject(f)) {
      add(f.country_name);
      add(f.countryName);
    }
  }
  return bag;
}

/** Nested object: ids that correspond to `/general-info/regions/:country` district picklists. */
function districtNumericIdFromObject(o: Record<string, unknown>): number | null {
  for (const k of [
    'state_id',
    'StateId',
    'stateId',
    'district_id',
    'districtId',
    'DistrictId',
    'region_id',
    'regionId',
    'RegionId',
  ] as const) {
    const n = coerceNumericField(o[k]);
    if (n !== null) {
      return n;
    }
  }
  for (const nestKey of ['region', 'Region', 'district', 'District', 'state', 'State'] as const) {
    const nest = o[nestKey];
    if (isPlainObject(nest)) {
      const n =
        coerceNumericField(nest.id) ??
        coerceNumericField(nest.region_id) ??
        coerceNumericField(nest.district_id) ??
        coerceNumericField(nest.state_id);
      if (n !== null) {
        return n;
      }
    }
  }
  return null;
}

/**
 * District filter id for list rows: root first, then `address`, then `facility` / `Facility` / `asset`,
 * mirroring {@link assetRecordListCountryId}.
 */
function assetRecordListDistrictFilterId(asset: Record<string, unknown>): number | null {
  const root = districtNumericIdFromObject(asset);
  if (root !== null) {
    return root;
  }
  const addr = asset.address;
  if (isPlainObject(addr)) {
    const n = districtNumericIdFromObject(addr);
    if (n !== null) {
      return n;
    }
  }
  for (const nk of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nk];
    if (isPlainObject(nested)) {
      const n = districtNumericIdFromObject(nested);
      if (n !== null) {
        return n;
      }
    }
  }
  return null;
}

function normalizedDistrictNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.state_name);
  add(asset.stateName);
  add(asset.district_name);
  add(asset.districtName);
  add(asset.region_name);
  add(asset.regionName);

  const ingestObject = (obj: Record<string, unknown>) => {
    add(obj.name);
    add(obj.label);
    add(obj.title);
    add(obj.region_name);
    add(obj.regionName);
    add(obj.state_name);
    add(obj.stateName);
    add(obj.district_name);
    add(obj.districtName);
    if (typeof obj.country === 'string') {
      add(obj.country);
    }
  };

  const maybeObjs = [
    asset.address,
    asset.region,
    asset.Region,
    asset.district,
    asset.District,
    asset.state,
    asset.State,
    asset.facility,
    asset.Facility,
  ];
  for (const mo of maybeObjs) {
    if (isPlainObject(mo)) {
      ingestObject(mo);
    }
  }
  return bag;
}

function assetRowMatchesSelectedDistrict(
  asset: Record<string, unknown>,
  wantId: number,
  catalogLabelLower: string | null,
): boolean {
  const rowId = assetRecordListDistrictFilterId(asset);
  if (rowId !== null && rowId === wantId) {
    return true;
  }
  if (!catalogLabelLower) {
    return false;
  }
  const names = normalizedDistrictNameBag(asset);
  if (names.has(catalogLabelLower)) {
    return true;
  }
  for (const n of Array.from(names)) {
    if (catalogLabelLower.length >= 2 && (n.includes(catalogLabelLower) || catalogLabelLower.includes(n))) {
      return true;
    }
  }
  return false;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** PATCH payloads are usually partial; reload GET so listing views match {@link AssetGetByIdView} (e.g. facilities). */
async function fetchFullAssetPayloadAfterSuccessfulPatch(assetIdTrimmed: string): Promise<Record<string, unknown> | null> {
  try {
    const refreshed = await getAssetById(assetIdTrimmed);
    return isPlainObject(refreshed) ? refreshed : null;
  } catch {
    return null;
  }
}

/** Wire fields overlaid onto baseline + PATCH JSON so Facility / add-ons etc. refresh even when GET-after-PATCH fails. */
const ASSET_DISPLAY_OVERLAY_AFTER_PATCH_KEYS = [
  'asset_name',
  'sub_title',
  'address',
  'country_id',
  'state_id',
  'asset_type_id',
  'latitude',
  'longitude',
  'min_capacity',
  'max_capacity',
  'phone_number',
  'email',
  'description',
  'facilities',
  'addons',
  'pricing_methods',
  'opening_times',
  'images',
  'asset_status',
] as const;

/** When GET-after-PATCH is unavailable: merge PATCH JSON with editor draft on the last GET snapshot used to open Edit. */
function mergeAssetDisplayPayloadAfterPatch(
  patchResult: unknown,
  editorDraft: Record<string, unknown>,
  baseline: Record<string, unknown> | null,
  options: { skipImagesEditorOverlay: boolean },
): Record<string, unknown> {
  const merged: Record<string, unknown> = baseline !== null ? deepClone(baseline) : {};
  if (isPlainObject(patchResult)) {
    Object.assign(merged, deepClone(patchResult));
  }
  for (const key of ASSET_DISPLAY_OVERLAY_AFTER_PATCH_KEYS) {
    if (options.skipImagesEditorOverlay && key === 'images') {
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(editorDraft, key)) {
      merged[key] = deepClone(editorDraft[key]);
    }
  }
  return merged;
}
/**
 * List/detail payloads vary: `asset_type_id`, camelCase, nested `asset_type`, or name-only.
 */
function assetRecordListAssetTypeId(asset: Record<string, unknown>): number | null {
  for (const k of [
    'asset_type_id',
    'assetTypeId',
    'AssetTypeId',
    'type_id',
    'TypeId',
    'facility_type_id',
  ] as const) {
    const n = coerceNumericField((asset as Record<string, unknown>)[k]);
    if (n !== null) {
      return n;
    }
  }
  const nested = asset.asset_type;
  if (isPlainObject(nested)) {
    return (
      coerceNumericField(nested.id) ??
      coerceNumericField(nested.asset_type_id) ??
      coerceNumericField(nested.type_id)
    );
  }
  return null;
}

function normalizedAssetTypeNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    const t = raw.trim().toLowerCase();
    bag.add(t);
  };
  add(asset.asset_type_name);
  add(asset.assetTypeName);
  add(asset.type_name);
  add(asset.type);
  if (typeof asset.Type === 'string') {
    add(asset.Type);
  }
  if (typeof asset.asset_type === 'string') {
    add(asset.asset_type);
  }
  if (isPlainObject(asset.asset_type)) {
    const o = asset.asset_type;
    add(o.name);
    add(o.label);
    add(o.type_name);
    add(o.title);
  }
  return bag;
}

/** Match selected catalog id, or label text when the API omits ids on list rows. */
function assetRowMatchesSelectedAssetType(
  asset: Record<string, unknown>,
  wantId: number,
  catalogLabelLower: string | null,
): boolean {
  const rowId = assetRecordListAssetTypeId(asset);
  if (rowId !== null && rowId === wantId) {
    return true;
  }
  if (catalogLabelLower) {
    const names = normalizedAssetTypeNameBag(asset);
    if (names.has(catalogLabelLower)) {
      return true;
    }
    for (const n of Array.from(names)) {
      if (n.includes(catalogLabelLower) || catalogLabelLower.includes(n)) {
        return true;
      }
    }
  }
  return false;
}

/** Removed from old persisted drafts that may still be loaded for this owner. */
const LEGACY_DEMO_OPENING_TIME_DATE_KEYS = ['04-25', '--30', '2025-01-29'] as const;

const CREATE_BODY_AUTO_PERSIST_DEBOUNCE_MS = 400;

function stripLegacyDemoOpeningDateKeys(body: Record<string, unknown>): {
  body: Record<string, unknown>;
  changed: boolean;
} {
  const ot = body.opening_times;
  if (!isPlainObject(ot)) {
    return { body, changed: false };
  }
  let changed = false;
  const nextOt: Record<string, unknown> = { ...ot };
  for (const k of LEGACY_DEMO_OPENING_TIME_DATE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(nextOt, k)) {
      delete nextOt[k];
      changed = true;
    }
  }
  if (!changed) {
    return { body, changed: false };
  }
  return { body: { ...body, opening_times: nextOt }, changed: true };
}

function reconciledCreateImageDraftState(
  loginId?: string | null,
  images: unknown = loadPersistedAssetCreateAttempt(loginId)?.images,
): { paths: string[]; previewSrcs: (string | undefined)[] } {
  return reconcileDraftCreateImages(
    normalizedCreateImagesPaths(sanitizeCreateEditorImagePaths(images)),
    loadPersistedAssetCreateImagePreviews(loginId),
  );
}

function persistAssetCreateDraft(
  body: Record<string, unknown>,
  loginId?: string | null,
): void {
  persistAssetCreateAttempt(
    {
      ...body,
      images: sanitizeCreateEditorImagePaths(body.images),
    },
    loginId,
  );
}

const ASSET_CREATE_EDITOR_EXCLUDED_KEYS = new Set([
  'facilities',
  'addons',
  'pricing_methods',
  'images',
  'opening_times',
]);

/** Ensures create-form scalars (e.g. `email`) exist even after update drafts or old persisted bodies. */
function ensureAssetCreateFormScalars(body: Record<string, unknown>): Record<string, unknown> {
  const defaults = emptyAssetCreateBody();
  const next: Record<string, unknown> = { ...body };
  for (const [key, defaultVal] of Object.entries(defaults)) {
    if (ASSET_CREATE_EDITOR_EXCLUDED_KEYS.has(key)) {
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = defaultVal;
    }
  }
  const defaultAddr = defaults.address;
  if (isPlainObject(defaultAddr)) {
    const curAddr = next.address;
    if (!isPlainObject(curAddr)) {
      next.address = deepClone(defaultAddr);
    } else {
      const mergedAddr: Record<string, unknown> = { ...deepClone(defaultAddr), ...curAddr };
      for (const [ak, av] of Object.entries(defaultAddr)) {
        if (!Object.prototype.hasOwnProperty.call(mergedAddr, ak)) {
          mergedAddr[ak] = av;
        }
      }
      next.address = mergedAddr;
    }
  }
  if (isAssetCreateOpeningTimesUnset(next.opening_times)) {
    next.opening_times = buildDefaultAssetCreateOpeningTimes();
  }
  return next;
}

/** Last persisted Create-submit body for this owner, or empty scalars when absent. */
function defaultAssetCreateFieldsTemplate(loginId?: string | null): Record<string, unknown> {
  const stored = loadPersistedAssetCreateAttempt(loginId);
  const draft = stored ?? emptyAssetCreateBody();
  const { body: withoutLegacyDemo } = stripLegacyDemoOpeningDateKeys(
    deepClone(draft) as Record<string, unknown>,
  );
  const body = stripOpeningTimesDateOverridesFromAssetBody(withoutLegacyDemo);
  const prepared = prepareAssetRequestBody(body);
  const base =
    prepared !== null && typeof prepared === 'object' && !Array.isArray(prepared)
      ? (prepared as Record<string, unknown>)
      : body;
  const result = applyAssetCreateContactDefaults(ensureAssetCreateFormScalars(base));
  const { paths } = reconciledCreateImageDraftState(loginId, result.images);
  result.images = paths;
  return result;
}

/** PATCH body: omit read-only / surrogate keys the GET response may include. */
const STRIP_FROM_ASSET_PATCH = new Set(['id', 'created_at', 'updated_at', 'deleted_at', 'asset_id']);

function cloneAssetRecordForUpdateBody(
  src: Record<string, unknown>,
  facilityCatalog: Record<string, unknown>[] = [],
): Record<string, unknown> {
  const raw = deepClone(src);
  STRIP_FROM_ASSET_PATCH.forEach((k) => {
    delete raw[k];
  });
  if ('facilities' in raw) {
    raw.facilities = normalizeFacilitiesWireForEditor(raw.facilities);
  }
  Object.assign(raw, migrateAssetBodyPromotedAddonsToFacilities(raw, facilityCatalog));
  if ('pricing_methods' in raw) {
    raw.pricing_methods = normalizePricingMethodsWireForEditor(raw.pricing_methods);
  }
  if ('images' in raw) {
    raw.images = normalizeAssetImagesToWireStrings(raw.images);
  }
  const withAddress = ensureAddressEditorShape(raw);
  const savedLat = withAddress.latitude;
  const savedLng = withAddress.longitude;
  const prepared = prepareAssetRequestBody(withAddress);
  if (prepared !== null && typeof prepared === 'object' && !Array.isArray(prepared)) {
    const out = prepared as Record<string, unknown>;
    if (savedLat !== undefined) {
      out.latitude = savedLat;
    }
    if (savedLng !== undefined) {
      out.longitude = savedLng;
    }
    return out;
  }
  return raw;
}

function facilityRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.facility_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function parseCreateBodyFacilityIds(value: unknown): Set<number> {
  if (!Array.isArray(value)) {
    return new Set();
  }
  const s = new Set<number>();
  for (const x of value) {
    if (typeof x === 'number' && Number.isFinite(x)) {
      s.add(x);
      continue;
    }
    if (typeof x === 'string' && x.trim()) {
      const n = Number(x.trim());
      if (Number.isFinite(n)) {
        s.add(n);
      }
    }
  }
  return s;
}

function orderedFacilitiesFromSelection(
  catalogRows: Record<string, unknown>[],
  selected: Set<number>,
): number[] {
  const out: number[] = [];
  for (const row of catalogRows) {
    const id = facilityRowNumericId(row);
    if (id !== null && selected.has(id)) {
      out.push(id);
    }
  }
  return out;
}

function coalesceFiniteNumber(...candidates: unknown[]): number | null {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) {
      return c;
    }
    if (typeof c === 'string' && c.trim()) {
      const n = Number(c.trim());
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  return null;
}

type CreateAssetPricingDraft = { price: number | null };

function pricingTypeRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.pricing_type_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function parseCreateBodyPricingMethods(value: unknown): Map<number, CreateAssetPricingDraft> {
  const m = new Map<number, CreateAssetPricingDraft>();
  if (!Array.isArray(value)) {
    return m;
  }
  for (const item of value) {
    if (!isPlainObject(item)) {
      continue;
    }
    const idRaw = item.pricing_type_id;
    const id =
      typeof idRaw === 'number' && Number.isFinite(idRaw)
        ? idRaw
        : typeof idRaw === 'string' && idRaw.trim()
          ? Number(idRaw.trim())
          : NaN;
    if (!Number.isFinite(id)) {
      continue;
    }
    const p = item.price;
    const price =
      typeof p === 'number' && Number.isFinite(p)
        ? p
        : typeof p === 'string' && p.trim()
          ? Number(p.trim())
          : null;
    m.set(id, {
      price: price !== null && Number.isFinite(price) ? price : null,
    });
  }
  return m;
}

function orderedPricingMethodsFromSelection(
  catalogRows: Record<string, unknown>[],
  byId: Map<number, CreateAssetPricingDraft>,
): Array<{ pricing_type_id: number; price: number | null }> {
  const out: Array<{ pricing_type_id: number; price: number | null }> = [];
  const seen = new Set<number>();
  for (const row of catalogRows) {
    const id = pricingTypeRowNumericId(row);
    if (id === null || !byId.has(id)) {
      continue;
    }
    const d = byId.get(id)!;
    out.push({ pricing_type_id: id, price: d.price });
    seen.add(id);
  }
  for (const id of Array.from(byId.keys())) {
    if (!seen.has(id)) {
      const d = byId.get(id)!;
      out.push({ pricing_type_id: id, price: d.price });
    }
  }
  return out;
}

function pricingCatalogDisplayTitle(row: Record<string, unknown>): string {
  const fromName =
    typeof row.pricing_type_name === 'string' && row.pricing_type_name.trim()
      ? row.pricing_type_name.trim()
      : typeof row.type_name === 'string' && row.type_name.trim()
        ? row.type_name.trim()
        : typeof row.name === 'string' && row.name.trim()
          ? row.name.trim()
          : '';
  if (fromName) {
    return fromName;
  }
  return assetRecordDisplayTitle(row);
}

/** GET payloads may return `facilities` as id rows; create/update expect `number[]`. */
function normalizeFacilitiesWireForEditor(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: number[] = [];
  for (const x of value) {
    if (typeof x === 'number' && Number.isFinite(x)) {
      out.push(x);
      continue;
    }
    if (typeof x === 'string' && x.trim()) {
      const n = Number(x.trim());
      if (Number.isFinite(n)) {
        out.push(n);
      }
      continue;
    }
    if (isPlainObject(x)) {
      const id = facilityRowNumericId(x);
      if (id !== null) {
        out.push(id);
      }
    }
  }
  return out;
}

function normalizePricingMethodsWireForEditor(
  value: unknown,
): Array<{ pricing_type_id: number; price: number | null }> {
  const m = parseCreateBodyPricingMethods(value);
  return Array.from(m.entries()).map(([id, d]) => ({
    pricing_type_id: id,
    price: d.price,
  }));
}

type ListFilterFieldsProps = {
  listFilterCountryId: string;
  setListFilterCountryId: (value: string) => void;
  setListFilterDistrictId: (value: string) => void;
  countriesStatus: 'idle' | 'loading' | 'ready' | 'error';
  countries: GeneralInfoPicklistItem[];
  countriesError: string | null;
  listFilterDistrictId: string;
  listFilterRegionsStatus: 'idle' | 'loading' | 'ready' | 'error';
  listFilterRegions: GeneralInfoPicklistItem[];
  listFilterRegionsError: string | null;
  listFilterAssetTypeId: string;
  setListFilterAssetTypeId: (value: string) => void;
  assetTypesStatus: 'idle' | 'loading' | 'ready' | 'error';
  assetTypes: GeneralInfoPicklistItem[];
  assetTypesError: string | null;
  listNameSearch: string;
  setListNameSearch: (value: string) => void;
  listBrowseShowsRegionFilter: boolean;
  listSortMode: AssetBrowseListSortMode;
  setListSortMode: (value: AssetBrowseListSortMode) => void;
};

function renderListFilterFields(
  menuId: string,
  fieldClass: string | undefined,
  headingClass: string | undefined,
  controlClass: string | undefined,
  language: AppLanguage,
  t: TranslateFn,
  props: ListFilterFieldsProps,
) {
  const {
    listFilterCountryId,
    setListFilterCountryId,
    setListFilterDistrictId,
    countriesStatus,
    countries,
    countriesError,
    listFilterDistrictId,
    listFilterRegionsStatus,
    listFilterRegions,
    listFilterRegionsError,
    listFilterAssetTypeId,
    setListFilterAssetTypeId,
    assetTypesStatus,
    assetTypes,
    assetTypesError,
    listNameSearch,
    setListNameSearch,
    listBrowseShowsRegionFilter,
    listSortMode,
    setListSortMode,
  } = props;

  return (
    <>
      <label className={fieldClass} htmlFor={`${menuId}-list-country`}>
        <span className={headingClass}>{t('assets.filter.country')}</span>
        <select
          id={`${menuId}-list-country`}
          className={controlClass}
          value={listFilterCountryId}
          onChange={(ev) => {
            setListFilterCountryId(ev.target.value);
            setListFilterDistrictId('');
          }}
          disabled={countriesStatus === 'loading'}
        >
          <option value="">{t('assets.filter.allCountries')}</option>
          {countries.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {localizeGeoLabel(c.label, language)}
            </option>
          ))}
        </select>
        {countriesStatus === 'error' && countriesError ? (
          <span className={styles.listFilterSubtleError} role="alert">
            {countriesError}
          </span>
        ) : null}
      </label>
      {listBrowseShowsRegionFilter ? (
        <label className={fieldClass} htmlFor={`${menuId}-list-district`}>
          <span className={headingClass}>{t('assets.filter.region')}</span>
          <select
            id={`${menuId}-list-district`}
            className={controlClass}
            value={listFilterDistrictId}
            onChange={(ev) => setListFilterDistrictId(ev.target.value)}
            disabled={
              listFilterCountryId === '' ||
              listFilterRegionsStatus === 'loading' ||
              (listFilterRegionsStatus === 'ready' && listFilterRegions.length === 0)
            }
          >
            <option value="">
              {listFilterCountryId === ''
                ? t('assets.filter.selectCountryFirst')
                : listFilterRegionsStatus === 'loading'
                  ? t('assets.filter.loadingRegions')
                  : listFilterRegions.length === 0
                    ? t('assets.filter.noRegions')
                    : t('assets.filter.allRegions')}
            </option>
            {listFilterRegions.map((r) => (
              <option key={r.id} value={String(r.id)}>
                {localizeGeoLabel(r.label, language)}
              </option>
            ))}
          </select>
          {listFilterRegionsStatus === 'error' && listFilterRegionsError ? (
            <span className={styles.listFilterSubtleError} role="alert">
              {listFilterRegionsError}
            </span>
          ) : null}
        </label>
      ) : null}
      <label className={fieldClass} htmlFor={`${menuId}-list-sort`}>
        <span className={headingClass}>{t('assets.filter.sort')}</span>
        <select
          id={`${menuId}-list-sort`}
          className={controlClass}
          value={listSortMode}
          onChange={(ev) => setListSortMode(ev.target.value as AssetBrowseListSortMode)}
        >
          <option value="distance">{t('assets.filter.sortDistance')}</option>
          <option value="name">{t('assets.filter.sortName')}</option>
          <option value="priceAsc">{t('assets.filter.sortPriceAsc')}</option>
          <option value="priceDesc">{t('assets.filter.sortPriceDesc')}</option>
        </select>
      </label>
      <label className={fieldClass} htmlFor={`${menuId}-list-asset-type`}>
        <span className={headingClass}>{t('assets.filter.assetType')}</span>
        <select
          id={`${menuId}-list-asset-type`}
          className={controlClass}
          value={listFilterAssetTypeId}
          onChange={(ev) => setListFilterAssetTypeId(ev.target.value)}
          disabled={assetTypesStatus === 'loading'}
        >
          <option value="">{t('assets.filter.allTypes')}</option>
          {assetTypes.map((typeItem) => (
            <option key={typeItem.id} value={String(typeItem.id)}>
              {localizeAssetTypeLabel(typeItem.label, language, t)}
            </option>
          ))}
        </select>
        {assetTypesStatus === 'error' && assetTypesError ? (
          <span className={styles.listFilterSubtleError} role="alert">
            {assetTypesError}
          </span>
        ) : null}
      </label>
      <label className={fieldClass} htmlFor={`${menuId}-list-search`}>
        <span className={headingClass}>{t('assets.filter.nameOrAddressContains')}</span>
        <input
          id={`${menuId}-list-search`}
          type="search"
          className={controlClass}
          value={listNameSearch}
          onChange={(ev) => setListNameSearch(ev.target.value)}
          placeholder={t('assets.filter.placeholderSubstring')}
          autoComplete="off"
        />
      </label>
    </>
  );
}

export function AssetsMenu({
  embedded = false,
  pageTheme = false,
}: {
  embedded?: boolean;
  pageTheme?: boolean;
} = {}) {
  const { t, language } = useI18n();
  const assetImageLimitMessages = useMemo(
    () => ({
      tooMany: (max: number) => t('assets.imagesTooMany', { max }),
      tooLarge: (fileName: string, sizeMb: string, maxMb: number) =>
        t('assets.imagesFileTooLarge', { name: fileName, sizeMb, maxMb }),
    }),
    [t],
  );
  const { userLoginType, isLoggedIn, userName } = useAuth();
  const {
    assetsBrowseParams,
    assetsTabFocus,
    clearAssetsBrowseAssetId,
    clearAssetsBrowseReturnView,
    openAssets,
    openBookings,
    openFavorites,
    openSearch,
    searchQuery,
    showAssetInBrowse,
    navigationBack,
  } = useAppNavigation();
  const assetsMenuAllowed = canUseAssetsMenu(userLoginType);
  const canMutateAssets = canMutateAssetsForLoginType(userLoginType);
  /** Owners browsing the public catalog from home / type listings (not Assets-tab manage mode). */
  const ownerCatalogBrowse = canMutateAssets && assetsBrowseParams.catalogBrowse;
  const assistantSlotChip = useMemo(
    () => assistantSearchChipFromBrowse(assetsBrowseParams, language),
    [
      assetsBrowseParams.city,
      assetsBrowseParams.whenDate,
      assetsBrowseParams.whenMonth,
      assetsBrowseParams.whenTime,
      assetsBrowseParams.street,
      assetsBrowseParams.open24h,
      assetsBrowseParams.price,
      assetsBrowseParams.schedule,
      language,
    ],
  );
  const assistantBookingDayKey = assetsBrowseParams.whenDate?.trim() || null;
  const assistantBookingStartTime = assetsBrowseParams.whenTime
    ? floorAssistantTimeToHour(assetsBrowseParams.whenTime)
    : null;
  const browseCityDisplay =
    assetsBrowseParams.city?.split(/\s*[-–]\s*/)[0]?.trim() || assetsBrowseParams.city?.trim() || '';
  const onSearchAllOfficesInCity = useCallback(() => {
    openAssets({
      ...assetsBrowseParams,
      open24h: false,
      catalogBrowse: true,
      assetId: null,
    });
  }, [assetsBrowseParams, openAssets]);
  const open24hEmptyMessage = browseCityDisplay
    ? t('assets.filter.noOpen24h', { city: browseCityDisplay })
    : t('assets.filter.noOpen24hGeneric');
  const open24hEmptyActionLabel = browseCityDisplay
    ? t('assets.filter.searchAllOffices', { city: browseCityDisplay })
    : t('assets.filter.searchAllOfficesGeneric');
  const ownerLoginId = isLoggedIn ? getStoredLoginIdentifier() : null;
  const { requestAssetQuickBook, pendingAssetQuickBook, clearPendingAssetQuickBook, requestQuickBooking } =
    useBookingCreate();
  const { guardBookingAction } = useProfileBookingGate();
  const { coordinates: viewerCoordinates } = useUserLocationContext();
  const localizedOperations = useMemo(() => assetOperations(t), [t]);
  const assetsMenuDisabledTitle = !assetsMenuAllowed
    ? isLoggedIn
      ? t('assets.availableRoles')
      : t('assets.signInToUse')
    : undefined;
  const operationChoices = useMemo(
    () => selectableOperations(canMutateAssets, localizedOperations),
    [canMutateAssets, localizedOperations],
  );
  const menuId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelScrollRef = useRef<HTMLDivElement>(null);
  const createSuccessNoticeRef = useRef<HTMLParagraphElement>(null);
  const updateSuccessNoticeRef = useRef<HTMLParagraphElement>(null);
  const panelErrorNoticeRef = useRef<HTMLParagraphElement>(null);
  const [updateSuccessAssetId, setUpdateSuccessAssetId] = useState<string | null>(null);
  const [sessionOwnerLogoSrc, setSessionOwnerLogoSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [operation, setOperation] = useState<Operation>(() => {
    const loginType = getStoredLoginType();
    if (canMutateAssetsForLoginType(loginType)) {
      return 'listMine';
    }
    if (canBrowseBookableAssets(loginType)) {
      return 'active';
    }
    return 'getById';
  });
  const [assetId, setAssetId] = useState(() => readLastWorkedAssetId() ?? DEMO_ASSET_ID);
  const [assetBody, setAssetBody] = useState<Record<string, unknown>>(() =>
    defaultAssetCreateFieldsTemplate(getStoredLoginIdentifier()),
  );
  const assetBodyRef = useRef(assetBody);
  assetBodyRef.current = assetBody;
  /** Bumps on every Clear to remount fields and body form (draft textareas, asset ID). */
  const [formEpoch, setFormEpoch] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AssetRequestError | null>(null);
  const [response, setResponse] = useState<{ operation: AssetPanelResponseOperation; data: unknown } | null>(
    null,
  );
  /** When set, list/active response shows full asset detail instead of the list. */
  const [listDetailAsset, setListDetailAsset] = useState<Record<string, unknown> | null>(null);
  /** Cancels in-flight GET when the user leaves list detail before it completes. */
  const listDetailFetchGenRef = useRef(0);
  /** Client browse list already loaded — home type picks only change filters, not refetch. */
  const activeListingReadyRef = useRef(false);
  /** Ignore the first assetsTabFocus after mount (bootstrap fetch runs in the embedded init effect). */
  const assetsTabFocusSeenRef = useRef<number | null>(null);
  const ownerListingFetchGenRef = useRef(0);
  /** Prefetch picklists + client-side filters for list operations (before/after Fetch). */
  const [listFilterCountryId, setListFilterCountryId] = useState('');
  const [listFilterDistrictId, setListFilterDistrictId] = useState('');
  const [listFilterAssetTypeId, setListFilterAssetTypeId] = useState('');
  const [listNameSearch, setListNameSearch] = useState('');
  const [listSortMode, setListSortMode] = useState<AssetBrowseListSortMode>(
    () => assetsBrowseParams.listSort ?? 'distance',
  );
  const requestedListSort = assetsBrowseParams.listSort ?? null;
  const appliedRequestedListSortRef = useRef<AssetBrowseListSortMode | null>(requestedListSort);
  useEffect(() => {
    const previous = appliedRequestedListSortRef.current;
    appliedRequestedListSortRef.current = requestedListSort;
    if (requestedListSort) {
      setListSortMode(requestedListSort);
    } else if (previous) {
      setListSortMode('distance');
    }
  }, [requestedListSort]);
  const [listFilterRegions, setListFilterRegions] = useState<GeneralInfoPicklistItem[]>([]);
  const [listFilterRegionsStatus, setListFilterRegionsStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [listFilterRegionsError, setListFilterRegionsError] = useState<string | null>(null);
  const [listDeleteBusyId, setListDeleteBusyId] = useState<string | null>(null);
  const [listEditBusyId, setListEditBusyId] = useState<string | null>(null);
  const [listAvailabilityBusyId, setListAvailabilityBusyId] = useState<string | null>(null);
  /**
   * Asset ids hidden after DELETE succeeds or returns HTTP 404 (already gone), while GET list may still return stale rows.
   * Each new list response prunes ids that no longer appear; Clear / switching operation resets.
   */
  const [deletedListAssetIds, setDeletedListAssetIds] = useState<Set<string>>(() => new Set());
  /** After a successful GET, the update form is shown with this data until the ID changes or you clear. */
  const [updateEditReady, setUpdateEditReady] = useState(false);
  const [updateLoadedForId, setUpdateLoadedForId] = useState<string | null>(null);
  /** After Display (GET), this id unlocks Edit / Delete until cleared or changed. Normalized lowercase trim. */
  const [displayReadyId, setDisplayReadyId] = useState<string | null>(null);
  const assetEditing = operation === 'getById' && updateEditReady;
  const [createFacilitiesCatalog, setCreateFacilitiesCatalog] = useState<Record<string, unknown>[]>([]);
  const [open24hFacilityIds, setOpen24hFacilityIds] = useState<number[]>([]);
  /** Facilities catalog for amenity-icon hard filters (kitchen / wifi / parking). */
  const [amenityFacilityCatalog, setAmenityFacilityCatalog] = useState<Record<string, unknown>[]>([]);
  const [createFacilitiesStatus, setCreateFacilitiesStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [createFacilitiesError, setCreateFacilitiesError] = useState<string | null>(null);
  const [createFacilitiesFetchNonce, setCreateFacilitiesFetchNonce] = useState(0);
  const [createTermsOpen, setCreateTermsOpen] = useState(false);
  const [createTermsAgreed, setCreateTermsAgreed] = useState(false);
  const [createPricingTypesCatalog, setCreatePricingTypesCatalog] = useState<Record<string, unknown>[]>([]);
  const [createPricingTypesStatus, setCreatePricingTypesStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [createPricingTypesError, setCreatePricingTypesError] = useState<string | null>(null);
  const [createPricingTypesFetchNonce, setCreatePricingTypesFetchNonce] = useState(0);
  /** Active listings used to median-suggest create prices by type/city. */
  const [createPricingMarketRecords, setCreatePricingMarketRecords] = useState<Record<string, unknown>[]>(
    [],
  );
  const [countries, setCountries] = useState<GeneralInfoPicklistItem[]>([]);
  const [countriesStatus, setCountriesStatus] = useState<GeneralInfoPicklistsProp['countriesStatus']>('idle');
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [regions, setRegions] = useState<GeneralInfoPicklistItem[]>([]);
  const [regionsStatus, setRegionsStatus] = useState<GeneralInfoPicklistsProp['regionsStatus']>('idle');
  const [regionsError, setRegionsError] = useState<string | null>(null);
  /** Avoid showing another country’s regions briefly when `country_id` changes mid-flight. */
  const lastFetchedRegionsCountryIdRef = useRef<number | null>(null);
  const [assetTypes, setAssetTypes] = useState<GeneralInfoPicklistItem[]>([]);
  /** Full `GET /assets/asset-types` rows — browse filter uses a subset; gym list matching needs every type id. */
  const [assetTypesCatalogFull, setAssetTypesCatalogFull] = useState<GeneralInfoPicklistItem[]>([]);
  const [assetTypesStatus, setAssetTypesStatus] = useState<
    NonNullable<GeneralInfoPicklistsProp['assetTypesStatus']>
  >('idle');
  const [assetTypesError, setAssetTypesError] = useState<string | null>(null);
  const browseFilterAssetTypeChoices = useMemo(
    () => browseFilterAssetTypes(assetTypes),
    [assetTypes],
  );
  const listBrowseShowsRegionFilter = assetBrowseShowsRegionFilter(listFilterCountryId);

  useEffect(() => {
    if (!listBrowseShowsRegionFilter && listFilterDistrictId !== '') {
      setListFilterDistrictId('');
    }
  }, [listBrowseShowsRegionFilter, listFilterDistrictId]);

  const proximityAnchor = assetsBrowseParams.proximityAnchor ?? null;
  const applyBrowseListSort = useCallback(
    (rows: Record<string, unknown>[]) => {
      const priceSort = listSortMode === 'priceAsc' || listSortMode === 'priceDesc';
      if (isAssetKeywordQueryActive(listNameSearch) && !priceSort) {
        return rows;
      }
      return sortAssetBrowseListRecords(rows, listSortMode, {
        viewer: proximityAnchor ?? viewerCoordinates,
        language,
      });
    },
    [listNameSearch, listSortMode, proximityAnchor, viewerCoordinates, language],
  );
  const close = useCallback(() => setOpen(false), []);

  /** Country/state/asset-type pickers whenever create or loaded update body is on screen. */
  const assetFormPicklists: GeneralInfoPicklistsProp | undefined = useMemo(() => {
    const geoReady =
      canMutateAssets &&
      ((operation === 'create') || assetEditing);
    if (!geoReady) {
      return undefined;
    }
    return {
      countries,
      countriesStatus,
      countriesError,
      regions,
      regionsStatus,
      regionsError,
      assetTypes,
      assetTypesStatus,
      assetTypesError,
    };
  }, [
    canMutateAssets,
    operation,
    assetEditing,
    countries,
    countriesStatus,
    countriesError,
    regions,
    regionsStatus,
    regionsError,
    assetTypes,
    assetTypesStatus,
    assetTypesError,
  ]);

  const listRecordsBase = useMemo(() => {
    if (!response || !isListLikePanelOperation(response.operation)) {
      return [];
    }
    const list = normalizeAssetListResponse(response.data);
    if (!canMutateAssets || deletedListAssetIds.size === 0) {
      return list;
    }
    return list.filter((a) => {
      const id = canonicalListAssetRowId(a);
      return !id || !deletedListAssetIds.has(id);
    });
  }, [response, deletedListAssetIds, canMutateAssets]);

  useEffect(() => {
    if (listRecordsBase.length > 0) {
      registerAssetThumbnailRecords(listRecordsBase);
    }
  }, [listRecordsBase]);

  useEffect(() => {
    if (listDetailAsset) {
      registerAssetVenueRecord(listDetailAsset);
    }
  }, [listDetailAsset]);

  const filteredListRecords = useMemo(() => {
    const preferred = preferRecordsMatchingAssetIds(
      listRecordsBase,
      assetsBrowseParams.assetIds,
      assetsBrowseParams.city,
    );
    if (preferred !== null) {
      let rows = preferred;
      const browseStreet = assetsBrowseParams.street?.trim() ?? '';
      if (browseStreet) {
        rows = rows.filter((a) => assetRecordMatchesBrowseStreet(a, browseStreet));
      }
      if (assetsBrowseParams.open24h) {
        rows = refineAssetsForOpen24hSearch(rows, open24hFacilityIds);
      }
      rows = refineAssetsForAmenityIcons(
        rows,
        assetsBrowseParams.amenityIcons as FacilityIconId[] | null,
        amenityFacilityCatalog,
      );
      rows = refineAssetsForAssistantSchedule(rows, assetsBrowseParams.schedule);
      const q = listNameSearch.trim();
      if (isAssetKeywordQueryActive(q)) {
        return applyBrowseListSort(refineAssetKeywordSearchResults(rows, q, language));
      }
      return applyBrowseListSort(rows);
    }
    let rows = listRecordsBase;
    if (listFilterCountryId !== '') {
      const want = Number(listFilterCountryId);
      if (Number.isFinite(want)) {
        const picked = countries.find((c) => Number(c.id) === want);
        const labelNorm =
          typeof picked?.label === 'string' && picked.label.trim()
            ? picked.label.trim().toLowerCase()
            : null;
        rows = rows.filter((a) => {
          const cid = assetRecordListCountryId(a);
          if (cid !== null && cid === want) {
            return true;
          }
          if (labelNorm !== null && normalizedCountryNameBag(a).has(labelNorm)) {
            return true;
          }
          return false;
        });
      }
    }
    if (listFilterDistrictId !== '' && listBrowseShowsRegionFilter) {
      const want = Number(listFilterDistrictId);
      if (Number.isFinite(want)) {
        const picked = listFilterRegions.find((r) => Number(r.id) === want);
        const labelNorm =
          typeof picked?.label === 'string' && picked.label.trim()
            ? picked.label.trim().toLowerCase()
            : null;
        rows = rows.filter((a) => assetRowMatchesSelectedDistrict(a, want, labelNorm));
      }
    }
    if (listFilterAssetTypeId !== '') {
      const want = Number(listFilterAssetTypeId);
      if (Number.isFinite(want)) {
        const explicitLabel =
          assetsBrowseParams.assetTypeId === want ? assetsBrowseParams.assetTypeLabel : null;
        const labelNorm = browseFilterLabelForTypeId(want, assetTypes, explicitLabel);
        const gymCatalog =
          assetTypesCatalogFull.length > 0 ? assetTypesCatalogFull : assetTypes;
        if (assetsBrowseParams.officeFamily && (listFilterAssetTypeId === '' || isPrivateOfficeBrowseTypeSelection(want, labelNorm))) {
          const officeIds = buildOfficeVoiceSearchTypeIdSet(gymCatalog);
          rows = rows.filter((a) => assetMatchesOfficeVoiceSearch(a, gymCatalog, officeIds));
        } else if (isHomeGymBrowseTypeSelection(want, labelNorm)) {
          const { gymIds } = buildHomeCarouselTypeIdSets(gymCatalog);
          rows = rows.filter((a) => assetMatchesHomeGymCarousel(a, gymCatalog, gymIds));
        } else {
          rows = rows.filter((a) => assetRowMatchesSelectedAssetType(a, want, labelNorm));
        }
      }
    }
    const browseCity = assetsBrowseParams.city?.trim() ?? '';
    if (browseCity) {
      rows = rows.filter((a) => assetRecordMatchesBrowseCity(a, browseCity));
    }
    const browseStreet = assetsBrowseParams.street?.trim() ?? '';
    if (browseStreet) {
      rows = rows.filter((a) => assetRecordMatchesBrowseStreet(a, browseStreet));
    }
    if (assetsBrowseParams.open24h) {
      rows = refineAssetsForOpen24hSearch(rows, open24hFacilityIds);
    }
    rows = refineAssetsForAmenityIcons(
      rows,
      assetsBrowseParams.amenityIcons as FacilityIconId[] | null,
      amenityFacilityCatalog,
    );
    rows = refineAssetsForAssistantSchedule(rows, assetsBrowseParams.schedule);
    const q = listNameSearch.trim();
    if (isAssetKeywordQueryActive(q)) {
      rows = refineAssetKeywordSearchResults(rows, q, language);
    }
    rows = applyBrowseListSort(rows);
    return rows;
  }, [
    listRecordsBase,
    listFilterCountryId,
    listFilterDistrictId,
    listFilterRegions,
    listFilterAssetTypeId,
    listNameSearch,
    listBrowseShowsRegionFilter,
    assetTypes,
    assetTypesCatalogFull,
    countries,
    assetsBrowseParams.assetTypeId,
    assetsBrowseParams.assetTypeLabel,
    assetsBrowseParams.officeFamily,
    assetsBrowseParams.open24h,
    assetsBrowseParams.amenityIcons,
    assetsBrowseParams.city,
    assetsBrowseParams.street,
    assetsBrowseParams.assetIds,
    assetsBrowseParams.schedule,
    open24hFacilityIds,
    amenityFacilityCatalog,
    language,
    applyBrowseListSort,
  ]);

  const clientBrowseActive =
    (!canMutateAssets || ownerCatalogBrowse) && operation === 'active';

  const clientBrowseUiState = useMemo(
    () => {
      const countryPick =
        listFilterCountryId !== ''
          ? countries.find((c) => Number(c.id) === Number(listFilterCountryId))
          : undefined;
      const districtPick =
        listFilterDistrictId !== ''
          ? listFilterRegions.find((r) => Number(r.id) === Number(listFilterDistrictId))
          : undefined;
      return assetSearchUiStateFromBrowseFilters({
        countryId: listFilterCountryId,
        districtId: listBrowseShowsRegionFilter ? listFilterDistrictId : '',
        assetTypeId: listFilterAssetTypeId,
        text:
          isAssetKeywordQueryActive(listNameSearch) && !(assetsBrowseParams.assetIds?.length)
            ? listNameSearch
            : undefined,
        city: assetsBrowseParams.city,
        street: assetsBrowseParams.street,
        countryLabel: typeof countryPick?.label === 'string' ? countryPick.label : null,
        districtLabel: typeof districtPick?.label === 'string' ? districtPick.label : null,
        assetTypeLabel:
          browseFilterAssetTypeChoices.find((item) => String(item.id) === listFilterAssetTypeId)?.label ??
          assetsBrowseParams.assetTypeLabel,
        assetTypes,
        assetTypesCatalogFull,
        officeFamily: Boolean(assetsBrowseParams.officeFamily),
        open24h: Boolean(assetsBrowseParams.open24h),
        open24hFacilityIds,
        amenityIcons: (assetsBrowseParams.amenityIcons as FacilityIconId[] | null) ?? null,
        facilityCatalog: amenityFacilityCatalog,
        schedule: assetsBrowseParams.schedule ?? null,
        assetIds: assetsBrowseParams.assetIds,
        price: assetsBrowseParams.price ?? null,
      });
    },
    [
      listFilterCountryId,
      listFilterDistrictId,
      listFilterAssetTypeId,
      listNameSearch,
      listBrowseShowsRegionFilter,
      countries,
      listFilterRegions,
      assetsBrowseParams.assetTypeId,
      assetsBrowseParams.assetTypeLabel,
      assetsBrowseParams.officeFamily,
      assetsBrowseParams.open24h,
      assetsBrowseParams.amenityIcons,
      assetsBrowseParams.schedule,
      assetsBrowseParams.city,
      assetsBrowseParams.street,
      assetsBrowseParams.assetIds,
      assetsBrowseParams.price,
      open24hFacilityIds,
      amenityFacilityCatalog,
      browseFilterAssetTypeChoices,
      assetTypes,
      assetTypesCatalogFull,
    ],
  );

  const assistantHitsReady = isAssistantSearchHitsReady();
  // Amenity / open-weekday hard-filters need detail hydrate; list/hit snapshots are often
  // partial. Re-run catalog search instead of trusting hits.
  const amenityFilterActive = Boolean(assetsBrowseParams.amenityIcons?.length);
  const openWeekdayFilterActive = isAssistantOpenWeekdayFilter(assetsBrowseParams.schedule);
  const useAssistantHits =
    assistantHitsReady && !amenityFilterActive && !openWeekdayFilterActive;
  const clientBrowseSearchEnabled =
    clientBrowseActive && (open || (embedded && pageTheme)) && !useAssistantHits;

  const clientBrowseSearch = useBookableAssetSearch(clientBrowseUiState, {
    enabled: clientBrowseSearchEnabled,
    debounceMs: isAssetKeywordQueryActive(listNameSearch) ? ASSET_SEARCH_KEYWORD_DEBOUNCE_MS : 0,
    language,
  });

  const clientBrowseListView = useMemo(() => {
    if (!clientBrowseActive) {
      return null;
    }
    let records = useAssistantHits ? getAssistantSearchHits() : clientBrowseSearch.records;
    const placeKeyword = isAssetKeywordQueryActive(listNameSearch);
    const noLocality =
      !(assetsBrowseParams.city?.trim()) && !(assetsBrowseParams.street?.trim());
    const preferTypeOnPlace =
      !useAssistantHits &&
      placeKeyword &&
      noLocality &&
      Boolean(assetsBrowseParams.officeFamily || assetsBrowseParams.assetTypeLabel);
    if (preferTypeOnPlace) {
      records = records.filter(assetRecordIsBookable);
      records = refineAssetKeywordSearchResults(records, listNameSearch, language);
      const typed = hasClientSideBrowseFilters(clientBrowseUiState)
        ? filterAssetRecordsForUiState(records, clientBrowseUiState)
        : records;
      records = typed.length > 0 ? typed : records;
    } else {
      if (hasClientSideBrowseFilters(clientBrowseUiState)) {
        records = filterAssetRecordsForUiState(records, clientBrowseUiState);
      }
      records = records.filter(assetRecordIsBookable);
      if (isAssetKeywordQueryActive(listNameSearch)) {
        records = refineAssetKeywordSearchResults(records, listNameSearch, language);
      }
    }
    if (deletedListAssetIds.size > 0) {
      records = records.filter((a) => {
        const id = canonicalListAssetRowId(a);
        return !id || !deletedListAssetIds.has(id);
      });
    }
    records = refineAssetsForAmenityIcons(
      records,
      assetsBrowseParams.amenityIcons as FacilityIconId[] | null,
      amenityFacilityCatalog,
    );
    records = refineAssetsForAssistantSchedule(records, assetsBrowseParams.schedule);
    records = applyBrowseListSort(records);
    return {
      records,
      total: records.length,
    };
  }, [
    clientBrowseActive,
    clientBrowseSearch.records,
    clientBrowseUiState,
    deletedListAssetIds,
    assetsBrowseParams.amenityIcons,
    amenityFacilityCatalog,
    listNameSearch,
    language,
    applyBrowseListSort,
    useAssistantHits,
    assetsBrowseParams.schedule,
    assetsBrowseParams.city,
    assetsBrowseParams.street,
    assetsBrowseParams.officeFamily,
    assetsBrowseParams.assetTypeLabel,
  ]);

  const displayListRecords = clientBrowseListView?.records ?? filteredListRecords;
  const displayListRecordIdsKey = useMemo(
    () =>
      displayListRecords
        .map((a) => canonicalListAssetRowId(a))
        .filter((id): id is string => Boolean(id))
        .join('\n'),
    [displayListRecords],
  );

  useEffect(() => {
    if (!clientBrowseActive) {
      return;
    }
    // Follow-ups narrow the stored hits in memory; a first search waits for its own fetch.
    // Amenity filters re-fetch with hydrate — wait for that settle, not the hit list.
    if (!useAssistantHits && !clientBrowseSearch.settled) {
      return;
    }
    commitAssistantSearchHits(displayListRecords, { loaded: true });
  }, [
    useAssistantHits,
    clientBrowseActive,
    clientBrowseSearch.settled,
    displayListRecordIdsKey,
    displayListRecords,
  ]);

  useEffect(() => {
    if (clientBrowseActive && clientBrowseSearch.records.length > 0) {
      registerAssetThumbnailRecords(clientBrowseSearch.records);
    }
  }, [clientBrowseActive, clientBrowseSearch.records]);

  const useWideAssetPanel =
    operation === 'listMine' ||
    operation === 'active' ||
    (response?.operation === 'getById' && canRenderAssetGetByIdView(response.data)) ||
    listDetailAsset !== null ||
    (response !== null && isListLikePanelOperation(response.operation) && listRecordsBase.length > 0) ||
    assetEditing ||
    (operation === 'create' && canMutateAssets);

  const showAssetWireFieldsets =
    canMutateAssets && (operation === 'create' || assetEditing);

  const assetFormCurrency = useMemo(() => {
    const probe: Record<string, unknown> = {};
    const raw = assetBody.country_id;
    const cid =
      typeof raw === 'number' && Number.isFinite(raw)
        ? raw
        : typeof raw === 'string' && String(raw).trim()
          ? Number(String(raw).trim())
          : NaN;
    if (Number.isFinite(cid)) {
      probe.country_id = cid;
      const picked = countries.find((c) => Number(c.id) === cid);
      if (typeof picked?.label === 'string' && picked.label.trim()) {
        probe.country_name = picked.label.trim();
      }
    }
    return resolveAssetLocaleCurrency(probe);
  }, [assetBody.country_id, countries]);

  const assetGeoLabels = useMemo((): AddressGeocodeLabels => {
    const rawCountry = assetBody.country_id;
    const rawState = assetBody.state_id;
    const cid =
      typeof rawCountry === 'number' && Number.isFinite(rawCountry)
        ? rawCountry
        : typeof rawCountry === 'string' && String(rawCountry).trim()
          ? Number(String(rawCountry).trim())
          : NaN;
    const sid =
      typeof rawState === 'number' && Number.isFinite(rawState)
        ? rawState
        : typeof rawState === 'string' && String(rawState).trim()
          ? Number(String(rawState).trim())
          : NaN;
    const countryLabel =
      Number.isFinite(cid) ? countries.find((c) => Number(c.id) === cid)?.label ?? null : null;
    const regionLabel =
      Number.isFinite(sid) ? regions.find((r) => Number(r.id) === sid)?.label ?? null : null;
    return { countryLabel, regionLabel };
  }, [assetBody.country_id, assetBody.state_id, countries, regions]);

  useEffect(() => {
    if (!canMutateAssets || !isLoggedIn) {
      setSessionOwnerLogoSrc(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        setSessionOwnerLogoSrc(summary.ownerLogoSrc);
      } catch {
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          null,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        setSessionOwnerLogoSrc(summary.ownerLogoSrc);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canMutateAssets, isLoggedIn, userName, formEpoch]);

  useEffect(() => {
    if (!showAssetWireFieldsets || assetEditing) {
      return;
    }
    const query = buildAddressGeocodeQuery(assetBody, assetGeoLabels);
    if (!query) {
      return;
    }
    const timer = window.setTimeout(() => {
      void geocodeAssetAddressDetails(assetBody, assetGeoLabels)
        .then((hit) => {
        if (!hit) {
          return;
        }
        setAssetBody((prev) => {
          const prevCoords = parseAssetCoordinates(prev);
          const prevAddr = prev.address;
          const prevZip =
            prevAddr && typeof prevAddr === 'object' && !Array.isArray(prevAddr)
              ? (prevAddr as Record<string, unknown>).zip_code
              : undefined;
          const coordsUnchanged =
            prevCoords !== null &&
            Math.abs(prevCoords.lat - hit.lat) < 1e-6 &&
            Math.abs(prevCoords.lng - hit.lng) < 1e-6;
          const zipUnchanged =
            !hit.postalCode ||
            (typeof prevZip === 'string' && prevZip.trim() === hit.postalCode.trim());
          if (coordsUnchanged && zipUnchanged) {
            return prev;
          }
          let next = applyCoordinatesToAssetBody(prev, { lat: hit.lat, lng: hit.lng });
          if (hit.postalCode && !(typeof prevZip === 'string' && prevZip.trim())) {
            next = applyPostalCodeToAssetBody(next, hit.postalCode);
          }
          return next;
        });
      })
        .catch(() => {
          /* geocode proxy may be down — submit still validates coords */
        });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [
    showAssetWireFieldsets,
    assetGeoLabels,
    assetBody.address,
    assetBody.country_id,
    assetBody.state_id,
    assetEditing,
  ]);

  const resolveSubmitAssetBody = useCallback(async () => {
    try {
      if (assetEditing) {
        const baseline = assetEditorBaselineRecordRef.current ?? {};
        return await resolveAssetBodyGeoForUpdate(assetBody, baseline, assetGeoLabels);
      }
      return await resolveAssetBodyGeo(assetBody, assetGeoLabels);
    } catch (e) {
      if (e instanceof Error && e.message === 'GEOCODE_REQUIRED') {
        throw new Error(t('assets.geo.geocodeFailed'));
      }
      throw e;
    }
  }, [assetBody, assetEditing, assetGeoLabels, t]);

  const assetBodyForDynamicForm = useMemo(() => {
    if (operation === 'create') {
      let next = ensureAssetCreateFormScalars({ ...assetBody });
      delete next.facilities;
      delete next.addons;
      delete next.pricing_methods;
      delete next.images;
      delete next.opening_times;
      delete next.registration_date;
      delete next.latitude;
      delete next.longitude;
      delete next.email;
      delete next.phone_number;
      delete next.blackout_ranges;
      return assetCreateVisibleFormData(next);
    }
    if (assetEditing) {
      let next = assetEditVisibleFormData(assetBody);
      delete next.facilities;
      delete next.addons;
      delete next.pricing_methods;
      delete next.opening_times;
      return next;
    }
    return assetBody;
  }, [operation, assetEditing, assetBody]);

  const assetCreateMandatoryMarkers = useMemo(
    () => assetCreateMandatoryMarkerKeys(assetBody.country_id),
    [assetBody.country_id],
  );

  const assetUpdateMandatoryMarkers = useMemo(
    () => assetUpdateMandatoryMarkerKeys(assetBody.country_id),
    [assetBody.country_id],
  );

  const createOpeningTimesFormData = useMemo((): Record<string, unknown> => {
    const ot = assetBody.opening_times;
    if (isPlainObject(ot)) {
      return { opening_times: ot };
    }
    return { opening_times: {} };
  }, [assetBody.opening_times]);

  const createImagePaths = useMemo(
    () => normalizedCreateImagesPaths(assetBody.images),
    [assetBody.images],
  );

  const createMainImagePath = createImagePaths[0] ?? null;
  const createAdditionalImagePaths = createImagePaths.slice(1);

  const updateWireImageUrls = useMemo(
    () => normalizeAssetImagesToWireStrings(assetBody.images),
    [assetBody.images],
  );
  const updateMainImagePath = updateWireImageUrls[0] ?? null;
  const updateAdditionalImagePaths = updateWireImageUrls.slice(1);

  /** Parallel to {@link createImagePaths}: picked files for multipart submit. */
  const createImageFilesRef = useRef<(File | undefined)[]>([]);
  const [createImagePreviewSrcs, setCreateImagePreviewSrcs] = useState<(string | undefined)[]>(() =>
    reconciledCreateImageDraftState(getStoredLoginIdentifier()).previewSrcs,
  );
  const createImagePreviewSrcsRef = useRef(createImagePreviewSrcs);
  createImagePreviewSrcsRef.current = createImagePreviewSrcs;
  const createImagePreviewGenRef = useRef(0);
  const createImageDraftHydratedRef = useRef(false);

  /** Same layout as wire `images`, but filled only while editing update (multipart PATCH). */
  const updateImageFilesRef = useRef<(File | undefined)[]>([]);
  const [updateImagePreviewSrcs, setUpdateImagePreviewSrcs] = useState<(string | undefined)[]>([]);
  const updateImagePreviewSrcsRef = useRef(updateImagePreviewSrcs);
  updateImagePreviewSrcsRef.current = updateImagePreviewSrcs;
  const updateImagePreviewGenRef = useRef(0);

  const clearCreateImageFiles = useCallback(() => {
    createImageFilesRef.current = [];
    setCreateImagePreviewSrcs([]);
  }, []);

  const loadCreateFormFromDraft = useCallback((loginId?: string | null) => {
    const template = defaultAssetCreateFieldsTemplate(loginId);
    const { paths, previewSrcs } = reconciledCreateImageDraftState(loginId, template.images);
    createImageFilesRef.current = [];
    createImageDraftHydratedRef.current = true;
    setCreateImagePreviewSrcs(previewSrcs);
    setAssetBody({ ...template, images: paths });
  }, []);

  const clearUpdateImageFiles = useCallback(() => {
    updateImageFilesRef.current = [];
    setUpdateImagePreviewSrcs([]);
  }, []);

  const commitCreateImageSelection = useCallback(
    (merged: { paths: string[]; files: (File | undefined)[] }) => {
      createImageFilesRef.current = merged.files;
      const blobPreviewSrcs = merged.files.map((file) =>
        file ? URL.createObjectURL(file) : undefined,
      );
      setCreateImagePreviewSrcs(blobPreviewSrcs);
      setAssetBody((prev) => ({ ...prev, images: merged.paths }));

      const gen = ++createImagePreviewGenRef.current;
      void imageFilesToPreviewDataUrls(merged.files).then((previewSrcs) => {
        for (const src of blobPreviewSrcs) {
          if (src?.startsWith('blob:')) {
            URL.revokeObjectURL(src);
          }
        }
        if (createImagePreviewGenRef.current !== gen) {
          return;
        }
        setCreateImagePreviewSrcs(previewSrcs);
        persistAssetCreateImagePreviews(previewSrcs, ownerLoginId);
      });
    },
    [ownerLoginId],
  );

  const commitUpdateImageSelection = useCallback(
    (merged: { paths: string[]; files: (File | undefined)[] }) => {
      updateImageFilesRef.current = merged.files;
      setAssetBody((prev) => ({ ...prev, images: merged.paths }));
      const gen = ++updateImagePreviewGenRef.current;
      void imageFilesToPreviewDataUrls(merged.files).then((previewSrcs) => {
        if (updateImagePreviewGenRef.current !== gen) {
          return;
        }
        setUpdateImagePreviewSrcs(previewSrcs);
      });
    },
    [],
  );
  /** Full GET record when Edit was entered; used if refetch-after-PATCH fails so {@link AssetGetByIdView} can still render. */
  const assetEditorBaselineRecordRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (operation !== 'create') {
      createImageDraftHydratedRef.current = false;
      return;
    }
    if (!canMutateAssets || createImageDraftHydratedRef.current) {
      return;
    }
    createImageDraftHydratedRef.current = true;

    const paths = normalizedCreateImagesPaths(assetBodyRef.current.images);
    const { paths: nextPaths, previewSrcs } = reconciledCreateImageDraftState(
      ownerLoginId,
      paths,
    );
    if (nextPaths.join('\0') !== paths.join('\0')) {
      setAssetBody((prev) => ({ ...prev, images: nextPaths }));
    }
    setCreateImagePreviewSrcs(previewSrcs);
  }, [operation, canMutateAssets, ownerLoginId]);

  useEffect(() => {
    if (!assetEditing) {
      clearUpdateImageFiles();
    }
  }, [assetEditing, clearUpdateImageFiles]);

  const onCreateImagesFileInputChange = useCallback((ev: ReactChangeEvent<HTMLInputElement>) => {
    const input = ev.target;
    const list = input.files;
    if (!list || list.length === 0) {
      input.value = '';
      return;
    }
    /** `FileList` is live; clearing `input.value` later can empty it before React runs the updater. */
    const picked = Array.from(list);
    const existing = normalizedCreateImagesPaths(assetBodyRef.current.images);
    commitCreateImageSelection(mergeCreateImagePicks(existing, createImageFilesRef.current, picked));
    input.value = '';
  }, [commitCreateImageSelection]);

  /** Sets images[0] as the main image; all other images become "additional". */
  const onCreateMainImageFileInputChange = useCallback(
    (ev: ReactChangeEvent<HTMLInputElement>) => {
      const input = ev.target;
      const list = input.files;
      if (!list || list.length === 0) {
        input.value = '';
        return;
      }
      const picked = Array.from(list);
      const first = picked[0];
      if (!first) {
        input.value = '';
        return;
      }
      const path = imagePathSubmittedForCreate(first);
      if (!path) {
        input.value = '';
        return;
      }

      const existing = normalizedCreateImagesPaths(assetBodyRef.current.images);
      commitCreateImageSelection(
        mergeCreateMainImagePick(existing, createImageFilesRef.current, first, path),
      );

      input.value = '';
    },
    [commitCreateImageSelection],
  );

  const onUpdateImagesFileInputChange = useCallback((ev: ReactChangeEvent<HTMLInputElement>) => {
    const input = ev.target;
    const list = input.files;
    if (!list || list.length === 0) {
      input.value = '';
      return;
    }
    const picked = Array.from(list);
    const existing = normalizeAssetImagesToWireStrings(assetBodyRef.current.images);
    commitUpdateImageSelection(mergeCreateImagePicks(existing, updateImageFilesRef.current, picked));
    input.value = '';
  }, [commitUpdateImageSelection]);

  const onUpdateMainImageFileInputChange = useCallback(
    (ev: ReactChangeEvent<HTMLInputElement>) => {
      const input = ev.target;
      const list = input.files;
      if (!list || list.length === 0) {
        input.value = '';
        return;
      }
      const first = list[0];
      if (!first) {
        input.value = '';
        return;
      }
      const path = imagePathSubmittedForCreate(first);
      if (!path) {
        input.value = '';
        return;
      }
      const existing = normalizeAssetImagesToWireStrings(assetBodyRef.current.images);
      commitUpdateImageSelection(
        mergeCreateMainImagePick(existing, updateImageFilesRef.current, first, path),
      );
      input.value = '';
    },
    [commitUpdateImageSelection],
  );

  const onRemoveCreateImageAt = useCallback(
    (removeIndex: number) => {
      const imgs = normalizedCreateImagesPaths(assetBodyRef.current.images).filter(
        (_, i) => i !== removeIndex,
      );
      const nextFiles = createImageFilesRef.current.filter((_, i) => i !== removeIndex);
      commitCreateImageSelection({ paths: imgs, files: nextFiles });
    },
    [commitCreateImageSelection],
  );

  const removeUpdateWireImageAt = useCallback(
    (removeIndex: number) => {
      const imgs = normalizeAssetImagesToWireStrings(assetBodyRef.current.images).filter(
        (_, i) => i !== removeIndex,
      );
      const nextFiles = updateImageFilesRef.current.filter((_, i) => i !== removeIndex);
      commitUpdateImageSelection({ paths: imgs, files: nextFiles });
    },
    [commitUpdateImageSelection],
  );

  const selectedCreateFacilityIds = useMemo(
    () => parseCreateBodyFacilityIds(assetBody.facilities),
    [assetBody.facilities],
  );

  const partitionedCreateFacilities = useMemo(
    () => partitionAssetCreateFacilityCatalog(createFacilitiesCatalog),
    [createFacilitiesCatalog],
  );

  const onCreateFacilityToggle = useCallback(
    (facilityIds: number[], checked: boolean) => {
      setAssetBody((prev) => {
        const ids = parseCreateBodyFacilityIds(prev.facilities);
        for (const facilityId of facilityIds) {
          if (checked) {
            ids.add(facilityId);
          } else {
            ids.delete(facilityId);
          }
        }
        return {
          ...prev,
          facilities: orderedFacilitiesFromSelection(createFacilitiesCatalog, ids),
        };
      });
    },
    [createFacilitiesCatalog],
  );

  const selectedCreatePricingById = useMemo(
    () => parseCreateBodyPricingMethods(assetBody.pricing_methods),
    [assetBody.pricing_methods],
  );

  const createPricingSuggestionContext = useMemo(() => {
    const typeIdRaw = assetBody.asset_type_id;
    const assetTypeId =
      typeof typeIdRaw === 'number' && Number.isFinite(typeIdRaw)
        ? typeIdRaw
        : typeof typeIdRaw === 'string' && typeIdRaw.trim()
          ? Number(typeIdRaw.trim())
          : null;
    const typeId = assetTypeId !== null && Number.isFinite(assetTypeId) ? assetTypeId : null;
    const typeCatalog =
      assetTypesCatalogFull.length > 0 ? assetTypesCatalogFull : assetTypes;
    const typeLabel =
      typeId !== null
        ? typeCatalog.find((row) => Number(row.id) === typeId)?.label?.trim() || null
        : null;
    const address = isPlainObject(assetBody.address) ? assetBody.address : null;
    const city =
      address && typeof address.city === 'string' && address.city.trim()
        ? address.city.trim()
        : null;
    return {
      assetTypeId: typeId,
      assetTypeLabel: typeLabel,
      city,
      countryId: assetBody.country_id,
    };
  }, [
    assetBody.asset_type_id,
    assetBody.address,
    assetBody.country_id,
    assetTypes,
    assetTypesCatalogFull,
  ]);

  const createPriceSuggestion: AssetCreatePriceSuggestion | null = useMemo(() => {
    if (operation !== 'create' || !canMutateAssets) {
      return null;
    }
    if (!createPricingSuggestionContext.assetTypeId && !createPricingSuggestionContext.city) {
      return null;
    }
    return resolveAssetCreatePriceSuggestion({
      ...createPricingSuggestionContext,
      marketRecords: createPricingMarketRecords,
    });
  }, [
    operation,
    canMutateAssets,
    createPricingSuggestionContext,
    createPricingMarketRecords,
  ]);

  useEffect(() => {
    if (!open || operation !== 'create' || !canMutateAssets) {
      return;
    }
    let cancelled = false;
    void fetchActiveAssetCatalogStaged({ firstPages: 2, maxPages: 4 })
      .then((result) => {
        if (!cancelled) {
          setCreatePricingMarketRecords(result.records);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCreatePricingMarketRecords([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, operation, canMutateAssets]);

  const onCreatePricingTypeToggle = useCallback(
    (row: Record<string, unknown>, checked: boolean) => {
      setAssetBody((prev) => {
        const pid = pricingTypeRowNumericId(row);
        if (pid === null) {
          return prev;
        }
        const byId = parseCreateBodyPricingMethods(prev.pricing_methods);
        if (checked) {
          const suggested =
            createPriceSuggestion !== null
              ? suggestedPriceForPricingTypeId(createPriceSuggestion, pid)
              : null;
          byId.set(pid, {
            price: suggested !== null ? suggested : null,
          });
        } else {
          byId.delete(pid);
        }
        return {
          ...prev,
          pricing_methods: orderedPricingMethodsFromSelection(createPricingTypesCatalog, byId),
        };
      });
    },
    [createPricingTypesCatalog, createPriceSuggestion],
  );

  const onApplyCreatePriceSuggestion = useCallback(() => {
    if (!createPriceSuggestion) {
      return;
    }
    setAssetBody((prev) => {
      const byId = parseCreateBodyPricingMethods(prev.pricing_methods);
      const next = applySuggestionToPricingSelection(
        createPricingTypesCatalog,
        byId,
        createPriceSuggestion,
      );
      return {
        ...prev,
        pricing_methods: orderedPricingMethodsFromSelection(createPricingTypesCatalog, next),
      };
    });
  }, [createPriceSuggestion, createPricingTypesCatalog]);

  const onCreatePricingPriceChange = useCallback(
    (pricingTypeId: number, price: number | null) => {
      setAssetBody((prev) => {
        const byId = parseCreateBodyPricingMethods(prev.pricing_methods);
        const cur = byId.get(pricingTypeId);
        if (!cur) {
          return prev;
        }
        byId.set(pricingTypeId, { price });
        return {
          ...prev,
          pricing_methods: orderedPricingMethodsFromSelection(createPricingTypesCatalog, byId),
        };
      });
    },
    [createPricingTypesCatalog],
  );

  const panelEmptyHint = emptyHintText(operation, canMutateAssets, t);

  const prevOwnerLoginIdRef = useRef(ownerLoginId);
  useEffect(() => {
    if (prevOwnerLoginIdRef.current === ownerLoginId) {
      return;
    }
    prevOwnerLoginIdRef.current = ownerLoginId;
    if (operation !== 'create' || !canMutateAssets) {
      return;
    }
    clearCreateImageFiles();
    loadCreateFormFromDraft(ownerLoginId);
    setFormEpoch((k) => k + 1);
  }, [ownerLoginId, operation, canMutateAssets, loadCreateFormFromDraft]);

  /** Persist create drafts (including opening times) whenever fields change; debounced to limit writes. */
  useEffect(() => {
    if (operation !== 'create' || !canMutateAssets) {
      return;
    }
    const t = window.setTimeout(() => {
      persistAssetCreateDraft(deepClone(assetBody), ownerLoginId);
      persistAssetCreateImagePreviews(createImagePreviewSrcsRef.current, ownerLoginId);
    }, CREATE_BODY_AUTO_PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [operation, canMutateAssets, assetBody, ownerLoginId, createImagePreviewSrcs]);

  useEffect(() => {
    if (operation === 'create' || assetEditing) {
      return;
    }
    setCreateFacilitiesCatalog([]);
    setCreateFacilitiesStatus('idle');
    setCreateFacilitiesError(null);
    setCreatePricingTypesCatalog([]);
    setCreatePricingTypesStatus('idle');
    setCreatePricingTypesError(null);
    setCreatePricingMarketRecords([]);
    setCountries([]);
    setCountriesStatus('idle');
    setCountriesError(null);
    setRegions([]);
    setRegionsStatus('idle');
    setRegionsError(null);
    setAssetTypes([]);
    setAssetTypesCatalogFull([]);
    setAssetTypesStatus('idle');
    setAssetTypesError(null);
  }, [operation, assetEditing]);

  useEffect(() => {
    if (!assetsBrowseParams.open24h) {
      setOpen24hFacilityIds([]);
      return;
    }
    let cancelled = false;
    void fetchAssetFacilities()
      .then((facilitiesRaw) => {
        if (cancelled) {
          return;
        }
        setOpen24hFacilityIds(facilityCatalog24x7Ids(normalizeAssetListResponse(facilitiesRaw)));
      })
      .catch(() => {
        if (!cancelled) {
          setOpen24hFacilityIds([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assetsBrowseParams.open24h]);

  useEffect(() => {
    const icons = assetsBrowseParams.amenityIcons;
    if (!icons?.length) {
      setAmenityFacilityCatalog([]);
      return;
    }
    let cancelled = false;
    void fetchAssetFacilities()
      .then((facilitiesRaw) => {
        if (cancelled) {
          return;
        }
        setAmenityFacilityCatalog(normalizeAssetListResponse(facilitiesRaw));
      })
      .catch(() => {
        if (!cancelled) {
          setAmenityFacilityCatalog([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assetsBrowseParams.amenityIcons]);

  useEffect(() => {
    if (!open || !canMutateAssets) {
      return;
    }
    if (operation !== 'create' && !assetEditing) {
      return;
    }
    let cancelled = false;
    setCreateFacilitiesStatus('loading');
    setCreateFacilitiesError(null);
    (async () => {
      try {
        const facilitiesRaw = await fetchAssetFacilities();
        if (cancelled) {
          return;
        }
        const ordered: Record<string, unknown>[] = [];
        const seen = new Set<number>();
        for (const row of normalizeAssetListResponse(facilitiesRaw)) {
          const nid = facilityRowNumericId(row);
          if (nid === null || seen.has(nid)) {
            continue;
          }
          seen.add(nid);
          ordered.push(row);
        }
        setCreateFacilitiesCatalog(ordered);
        setCreateFacilitiesStatus('ready');
      } catch (err) {
        if (cancelled) {
          return;
        }
        setCreateFacilitiesStatus('error');
        setCreateFacilitiesCatalog([]);
        setCreateFacilitiesError(
          err instanceof AssetRequestError
            ? shortAssetRequestErrorSummary(err)
            : err instanceof Error
              ? err.message
              : t('assets.failedFacilities'),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, operation, canMutateAssets, assetEditing, createFacilitiesFetchNonce]);

  useEffect(() => {
    if (!open || !canMutateAssets) {
      return;
    }
    if (operation !== 'create' && !assetEditing) {
      return;
    }
    if (createFacilitiesStatus !== 'ready') {
      return;
    }
    setAssetBody((prev) => {
      const next = migrateAssetBodyPromotedAddonsToFacilities(prev, createFacilitiesCatalog);
      const prevAddons = JSON.stringify(prev.addons ?? []);
      const nextAddons = JSON.stringify(next.addons ?? []);
      const prevFacilities = JSON.stringify(prev.facilities ?? []);
      const nextFacilities = JSON.stringify(next.facilities ?? []);
      if (prevAddons === nextAddons && prevFacilities === nextFacilities) {
        return prev;
      }
      return next;
    });
  }, [
    open,
    operation,
    canMutateAssets,
    assetEditing,
    createFacilitiesStatus,
    createFacilitiesCatalog,
  ]);

  useEffect(() => {
    if (!open || !canMutateAssets) {
      return;
    }
    if (operation !== 'create' && !assetEditing) {
      return;
    }
    let cancelled = false;
    setCreatePricingTypesStatus('loading');
    setCreatePricingTypesError(null);
    (async () => {
      try {
        const raw = await fetchAssetPricingTypes();
        if (cancelled) {
          return;
        }
        const rows = normalizeAssetListResponse(raw);
        const ordered: Record<string, unknown>[] = [];
        const seen = new Set<number>();
        for (const row of rows) {
          const nid = pricingTypeRowNumericId(row);
          if (nid === null || seen.has(nid)) {
            continue;
          }
          seen.add(nid);
          ordered.push(row);
        }
        setCreatePricingTypesCatalog(filterPricingTypesForAssetCreate(ordered));
        setCreatePricingTypesStatus('ready');
      } catch (err) {
        if (cancelled) {
          return;
        }
        setCreatePricingTypesStatus('error');
        setCreatePricingTypesCatalog([]);
        setCreatePricingTypesError(
          err instanceof AssetRequestError
            ? shortAssetRequestErrorSummary(err)
            : err instanceof Error
              ? err.message
              : t('assets.failedPricing'),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, operation, canMutateAssets, assetEditing, createPricingTypesFetchNonce]);

  useEffect(() => {
    if (!open || !canMutateAssets) {
      return;
    }
    if (operation !== 'create' && !assetEditing) {
      return;
    }
    if (createPricingTypesStatus !== 'ready') {
      return;
    }
    setAssetBody((prev) => {
      const next = stripDisallowedPricingMethodsFromAssetBody(prev, createPricingTypesCatalog);
      if (JSON.stringify(prev.pricing_methods ?? []) === JSON.stringify(next.pricing_methods ?? [])) {
        return prev;
      }
      return next;
    });
  }, [
    open,
    operation,
    canMutateAssets,
    assetEditing,
    createPricingTypesStatus,
    createPricingTypesCatalog,
  ]);

  useEffect(() => {
    const countriesWanted =
      open &&
      (operation === 'listMine' ||
        operation === 'active' ||
        (canMutateAssets && (operation === 'create' || assetEditing)));
    if (!countriesWanted) {
      return;
    }
    setCountriesStatus('loading');
    setCountriesError(null);
    let cancelled = false;

    async function load() {
      try {
        const headers = await buildAuthorizedHeaders({ Accept: 'application/json' });
        const list = await loadGeneralInfoCountries(headers);
        if (cancelled) {
          return;
        }
        setCountries(list);
        setCountriesStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setCountries([]);
          setCountriesStatus('error');
          setCountriesError(err instanceof Error ? err.message : t('assets.failedCountries'));
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, operation, assetEditing, canMutateAssets]);

  useEffect(() => {
    const assetTypesWanted =
      open &&
      (operation === 'listMine' ||
        operation === 'active' ||
        (canMutateAssets &&
          (operation === 'create' || assetEditing)));
    if (!assetTypesWanted) {
      return;
    }
    let cancelled = false;

    async function load() {
      setAssetTypesStatus('loading');
      setAssetTypesError(null);
      try {
        const list =
          operation === 'listMine' || operation === 'active'
            ? await fetchAssetTypesForBrowse()
            : await fetchAssetTypes();
        if (cancelled) {
          return;
        }
        if (operation === 'listMine' || operation === 'active') {
          setAssetTypesCatalogFull(list);
          setAssetTypes(browseFilterAssetTypes(list));
        } else {
          setAssetTypesCatalogFull([]);
          setAssetTypes(list);
        }
        setAssetTypesStatus('ready');
        setAssetTypesError(null);
      } catch (err) {
        if (!cancelled) {
          setAssetTypesCatalogFull([]);
          setAssetTypes(browseFilterAssetTypes([]));
          setAssetTypesStatus('error');
          setAssetTypesError(
            err instanceof AssetRequestError
              ? shortAssetRequestErrorSummary(err)
              : err instanceof Error
                ? err.message
                : t('assets.failedAssetTypes'),
          );
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, operation, canMutateAssets, assetEditing]);

  useEffect(() => {
    if (!open || (operation !== 'listMine' && operation !== 'active')) {
      return;
    }
    const countryId =
      listFilterCountryId === '' ? NaN : Number(listFilterCountryId);
    if (!Number.isFinite(countryId) || !assetBrowseShowsRegionFilter(countryId)) {
      setListFilterRegions([]);
      setListFilterRegionsStatus('idle');
      setListFilterRegionsError(null);
      return;
    }

    setListFilterRegionsStatus('loading');
    setListFilterRegionsError(null);

    let cancelled = false;

    async function load() {
      try {
        const headers = await buildAuthorizedHeaders({ Accept: 'application/json' });
        const list = await loadGeneralInfoRegions(countryId, headers);
        if (cancelled) {
          return;
        }
        setListFilterRegions(list);
        setListFilterRegionsStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setListFilterRegions([]);
          setListFilterRegionsStatus('error');
          setListFilterRegionsError(err instanceof Error ? err.message : t('assets.failedDistricts'));
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, operation, listFilterCountryId]);

  useEffect(() => {
    const geoActive =
      open &&
      canMutateAssets &&
      (operation === 'create' || assetEditing);
    if (!geoActive) {
      return;
    }
    const rawCountry = assetBody.country_id;
    const countryId =
      typeof rawCountry === 'number' && Number.isFinite(rawCountry)
        ? rawCountry
        : typeof rawCountry === 'string' && rawCountry.trim() !== ''
          ? Number(rawCountry.trim())
          : NaN;
    if (!Number.isFinite(countryId)) {
      setRegions([]);
      setRegionsStatus('idle');
      setRegionsError(null);
      lastFetchedRegionsCountryIdRef.current = null;
      return;
    }

    if (
      lastFetchedRegionsCountryIdRef.current != null &&
      lastFetchedRegionsCountryIdRef.current !== countryId
    ) {
      setRegions([]);
    }

    setRegionsStatus('loading');
    setRegionsError(null);

    let cancelled = false;

    async function load() {
      try {
        const headers = await buildAuthorizedHeaders({ Accept: 'application/json' });
        const list = await loadGeneralInfoRegions(countryId, headers);
        if (cancelled) {
          return;
        }
        setRegions(list);
        setRegionsStatus('ready');
        lastFetchedRegionsCountryIdRef.current = countryId;
        setAssetBody((prev) => {
          const sid = prev.state_id;
          const sidNum =
            typeof sid === 'number' && Number.isFinite(sid)
              ? sid
              : typeof sid === 'string' && sid.trim() !== ''
                ? Number(sid.trim())
                : NaN;
          if (list.length === 0) {
            return setAtPath(prev, ['state_id'], null);
          }
          if (Number.isFinite(sidNum) && !list.some((r) => Number(r.id) === sidNum)) {
            return setAtPath(prev, ['state_id'], null);
          }
          return prev;
        });
      } catch (err) {
        if (!cancelled) {
          setRegions([]);
          setRegionsStatus('error');
          setRegionsError(err instanceof Error ? err.message : t('assets.failedRegions'));
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, operation, assetEditing, canMutateAssets, assetBody.country_id]);

  useEffect(() => {
    if (!open || embedded) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    const onPointerDown = (e: globalThis.MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close, embedded]);

  useEffect(() => {
    if (!assetsMenuAllowed && !(embedded && pageTheme)) {
      setOpen(false);
    }
  }, [assetsMenuAllowed, embedded, pageTheme]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (operation === 'getById') {
      const lastDisplay = readLastWorkedAssetId();
      if (lastDisplay) {
        setAssetId(lastDisplay);
      }
    }
  }, [open, operation]);

  const prevClientLoginTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (canMutateAssets || !canBrowseBookableAssets(userLoginType)) {
      prevClientLoginTypeRef.current = userLoginType;
      return;
    }
    const switchedToClient = prevClientLoginTypeRef.current !== userLoginType;
    prevClientLoginTypeRef.current = userLoginType;
    if (!switchedToClient) {
      return;
    }
    setOperation('active');
    setDeletedListAssetIds(new Set());
    setListDetailAsset(null);
    setListFilterCountryId('');
    setListFilterDistrictId('');
    setListFilterAssetTypeId('');
    setListNameSearch('');
    setListFilterRegions([]);
    setListFilterRegionsStatus('idle');
    setListFilterRegionsError(null);
  }, [userLoginType, canMutateAssets]);

  useEffect(() => {
    if (canMutateAssets) {
      return;
    }
    if (!isClientBrowseOperation(operation)) {
      setOperation('active');
    }
  }, [operation, canMutateAssets]);

  useEffect(() => {
    if (updateLoadedForId === null) {
      return;
    }
    if (assetId.trim() !== updateLoadedForId) {
      setUpdateEditReady(false);
      setUpdateLoadedForId(null);
      setAssetBody(emptyAssetUpdateBody());
      setFormEpoch((k) => k + 1);
      assetEditorBaselineRecordRef.current = null;
    }
  }, [assetId, updateLoadedForId]);

  const displayedPlainForOwnerGate = useMemo(() => {
    if (response?.operation !== 'getById') {
      return null;
    }
    const d = response.data;
    return isPlainObject(d) ? d : null;
  }, [response]);

  useEffect(() => {
    if (response?.operation !== 'getById') {
      return;
    }
    const data = response.data;
    if (isPlainObject(data) && canRenderAssetGetByIdView(data)) {
      recordRecentlyViewedAsset(data);
    }
  }, [response]);

  const displayAssetOwnerGateOk = useMemo(() => {
    if (!displayedPlainForOwnerGate) {
      return true;
    }
    return canMutateAssetAsSessionOwner(displayedPlainForOwnerGate, getSessionToken());
  }, [displayedPlainForOwnerGate, isLoggedIn, userName]);

  const canPrepareAssetEdit = useMemo(
    () =>
      canMutateAssets &&
      Boolean(displayReadyId) &&
      normalizeAssetOpId(assetId) === displayReadyId &&
      response?.operation === 'getById' &&
      canRenderAssetGetByIdView(response.data) &&
      displayAssetOwnerGateOk,
    [
      assetId,
      canMutateAssets,
      displayAssetOwnerGateOk,
      displayReadyId,
      response,
    ],
  );

  const unifiedDeleteEnabled =
    operation === 'getById' &&
    canMutateAssets &&
    !assetEditing &&
    displayReadyId !== null &&
    normalizeAssetOpId(assetId) === displayReadyId &&
    displayAssetOwnerGateOk;

  const displayedAssetShowsOwnerMismatchHint = Boolean(
    operation === 'getById' &&
      !assetEditing &&
      displayedPlainForOwnerGate &&
      extractAssetOwnerId(displayedPlainForOwnerGate) &&
      !displayAssetOwnerGateOk,
  );

  const listDetailUpdateOwnerOk = useMemo(() => {
    if (listDetailAsset === null) {
      return false;
    }
    return canMutateAssetAsSessionOwner(listDetailAsset, getSessionToken());
  }, [listDetailAsset, isLoggedIn, userName]);

  const ownerScheduleAssetId = useMemo(() => {
    if (!canMutateAssets || ownerCatalogBrowse || assetEditing) {
      return null;
    }
    if (
      response?.operation === 'getById' &&
      isPlainObject(response.data) &&
      canRenderAssetGetByIdView(response.data) &&
      displayAssetOwnerGateOk
    ) {
      return assetRecordPrimaryId(response.data);
    }
    if (
      response &&
      isListLikePanelOperation(response.operation) &&
      listDetailAsset &&
      listDetailUpdateOwnerOk
    ) {
      return assetRecordPrimaryId(listDetailAsset);
    }
    return null;
  }, [
    assetEditing,
    canMutateAssets,
    displayAssetOwnerGateOk,
    listDetailAsset,
    listDetailUpdateOwnerOk,
    ownerCatalogBrowse,
    response,
  ]);

  const ownerScheduleAssetRecord = useMemo(() => {
    if (!ownerScheduleAssetId) {
      return null;
    }
    if (
      response?.operation === 'getById' &&
      isPlainObject(response.data) &&
      canRenderAssetGetByIdView(response.data)
    ) {
      return response.data;
    }
    if (listDetailAsset && assetRecordPrimaryId(listDetailAsset) === ownerScheduleAssetId) {
      return listDetailAsset;
    }
    return null;
  }, [ownerScheduleAssetId, response, listDetailAsset]);

  const showCloseInsteadOfDisplay = useMemo(
    () =>
      operation === 'getById' &&
      !updateEditReady &&
      response !== null &&
      response.operation === 'getById' &&
      displayReadyId !== null &&
      normalizeAssetOpId(assetId) === displayReadyId,
    [assetId, displayReadyId, operation, response, updateEditReady],
  );

  const showAssetIdAsShort = useMemo(
    () =>
      operation === 'getById' &&
      (assetEditing ||
        (displayReadyId !== null && normalizeAssetOpId(assetId) === displayReadyId)),
    [assetEditing, assetId, displayReadyId, operation],
  );

  const displayedGetByIdAsset = useMemo(() => {
    if (response?.operation !== 'getById' || !isPlainObject(response.data)) {
      return null;
    }
    const raw = response.data;
    const id = assetRecordPrimaryId(raw);
    const snapshot = id ? browseAssetListSnapshotForId(id) : null;
    const merged = snapshot ? mergeAssetApiRecordWithListSnapshot(raw, snapshot) : raw;
    return enrichAssetRecordAddressForDisplay(merged);
  }, [response]);

  const onCloseDisplayedView = useCallback(() => {
    setResponse(null);
    setDisplayReadyId(null);
    setUpdateSuccessAssetId(null);
    setError(null);
  }, []);

  const onPrepareAssetEdit = useCallback(() => {
    if (!canPrepareAssetEdit || !response || response.operation !== 'getById') {
      return;
    }
    const raw = response.data;
    if (!isPlainObject(raw)) {
      return;
    }
    const idTrim = assetId.trim();
    if (!idTrim) {
      return;
    }
    setResponse(null);
    setListDetailAsset(null);
    setAssetBody(cloneAssetRecordForUpdateBody(raw, createFacilitiesCatalog));
    assetEditorBaselineRecordRef.current = deepClone(raw);
    setUpdateSuccessAssetId(null);
    setUpdateEditReady(true);
    setUpdateLoadedForId(idTrim);
    persistLastWorkedAssetId(idTrim);
    setFormEpoch((k) => k + 1);
    requestAnimationFrame(() => {
      panelScrollRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
    });
  }, [assetId, canPrepareAssetEdit, createFacilitiesCatalog, response]);

  const onDiscardAssetEdit = useCallback(() => {
    setUpdateEditReady(false);
    setUpdateLoadedForId(null);
    setUpdateSuccessAssetId(null);
    assetEditorBaselineRecordRef.current = null;
    setAssetBody(emptyAssetUpdateBody());
    clearUpdateImageFiles();
    setFormEpoch((k) => k + 1);
  }, []);

  const clearListDetailAsset = useCallback(() => {
    setUpdateSuccessAssetId(null);
    if (assetsBrowseParams.returnView === 'favorites') {
      clearAssetsBrowseReturnView();
      openFavorites();
      return;
    }
    if (assetsBrowseParams.returnView === 'search') {
      const q = searchQuery?.trim();
      if (typeof window !== 'undefined' && window.history.length > 1) {
        navigationBack();
        return;
      }
      clearAssetsBrowseReturnView();
      openSearch(q || undefined);
      return;
    }
    if (assetsBrowseParams.returnView === 'bookings') {
      clearAssetsBrowseReturnView();
      openBookings();
      return;
    }
    if (assetsBrowseParams.assetId) {
      navigationBack();
      return;
    }
    listDetailFetchGenRef.current += 1;
    setListDetailAsset(null);
  }, [
    assetsBrowseParams.assetId,
    assetsBrowseParams.returnView,
    clearAssetsBrowseReturnView,
    navigationBack,
    openBookings,
    openFavorites,
    openSearch,
    searchQuery,
  ]);

  const urlSyncedAssetIdRef = useRef<string | null>(null);

  const enrichListDetailFromApi = useCallback((id: string, rowSnapshot: Record<string, unknown>) => {
    setListDetailAsset(prepareFavoriteAssetSnapshot(rowSnapshot));
    const gen = ++listDetailFetchGenRef.current;
    void (async () => {
      try {
        const loaded = await getAssetByIdBestEffort(rowSnapshot, id);
        if (gen !== listDetailFetchGenRef.current) {
          return;
        }
        if (isPlainObject(loaded)) {
          const merged = mergeAssetApiRecordWithListSnapshot(loaded, rowSnapshot);
          setListDetailAsset(prepareFavoriteAssetSnapshot(merged));
        }
      } catch {
        /* keep list row snapshot */
      }
    })();
  }, []);

  const onSelectListAsset = useCallback((asset: Record<string, unknown>) => {
    clearPendingAssetQuickBook();
    setUpdateSuccessAssetId(null);
    rememberBrowseAssetListSnapshot(asset);
    const id = canonicalListAssetRowId(asset);
    if (!id) {
      setListDetailAsset(prepareFavoriteAssetSnapshot(asset));
      return;
    }
    const normalized = normalizeAssetOpId(id);
    urlSyncedAssetIdRef.current = normalized;
    enrichListDetailFromApi(id, asset);
    if (embedded && pageTheme) {
      const current = normalizeAssetOpId(assetsBrowseParams.assetId ?? '');
      if (current !== normalized) {
        showAssetInBrowse(id);
      }
    }
  }, [
    embedded,
    pageTheme,
    assetsBrowseParams.assetId,
    showAssetInBrowse,
    enrichListDetailFromApi,
    clearPendingAssetQuickBook,
  ]);

  /** List row Book — open booking with the search/list row; do not block on GET /assets/:id. */
  const onBookFromList = useCallback(
    (asset: Record<string, unknown>) => {
      const id = assetRecordMutationId(asset);
      if (!id) {
        setError('This asset has no id — cannot start a booking.');
        return;
      }
      guardBookingAction(() => {
        setError(null);
        requestAssetQuickBook(id, asset);
        openBookings({ preserveCreateFlow: true });
      });
    },
    [guardBookingAction, requestAssetQuickBook, openBookings],
  );

  const onBookAssetFromDetail = useCallback(
    (asset: Record<string, unknown>) => {
      const id = assetRecordMutationId(asset);
      if (!id) {
        setError('This asset has no id — cannot start a booking.');
        return;
      }
      guardBookingAction(() => {
        setError(null);
        requestAssetQuickBook(id, asset);
      });
    },
    [guardBookingAction, requestAssetQuickBook],
  );

  const onInlineAssetQuickBookProceed = useCallback(
    async (draft: QuickBookingDraft) => {
      clearPendingAssetQuickBook();
      const snapshot = draft.assetSnapshot;
      const bookingAssetId = assetRecordMutationId(snapshot) ?? draft.assetId.trim();
      try {
        const fresh = await resolveAssetForBookingCreate(bookingAssetId, snapshot);
        requestQuickBooking({
          ...draft,
          assetId: bookingAssetId,
          assetSnapshot: fresh,
          createBody: { ...draft.createBody, asset_id: bookingAssetId },
        });
        openBookings({ preserveCreateFlow: true });
      } catch {
        setError(t('bookings.error.assetNotBookable'));
      }
    },
    [clearPendingAssetQuickBook, requestQuickBooking, openBookings, t],
  );

  const onInlineAssetQuickBookCancel = useCallback(() => {
    clearPendingAssetQuickBook();
  }, [clearPendingAssetQuickBook]);

  useEffect(() => {
    if (!embedded || !pageTheme) {
      return;
    }
    if (!assetsBrowseParams.assetId) {
      urlSyncedAssetIdRef.current = null;
      listDetailFetchGenRef.current += 1;
      setListDetailAsset(null);
    }
  }, [embedded, pageTheme, assetsBrowseParams.assetId]);

  const startAssetEditFromRecord = useCallback(
    async (id: string) => {
      setError(null);
      setLoading(true);
      setListEditBusyId(id);
      try {
        const loaded = await getAssetById(id);
        if (!isPlainObject(loaded)) {
          throw new Error(t('assets.cannotReadResponse'));
        }
        setResponse(null);
        setListDetailAsset(null);
        setOperation('getById');
        setAssetId(id);
        setAssetBody(cloneAssetRecordForUpdateBody(loaded, createFacilitiesCatalog));
        assetEditorBaselineRecordRef.current = deepClone(loaded);
        setUpdateSuccessAssetId(null);
        setUpdateEditReady(true);
        setUpdateLoadedForId(id);
        persistLastWorkedAssetId(id);
        setFormEpoch((k) => k + 1);
        requestAnimationFrame(() => {
          panelScrollRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
        });
      } catch (err) {
        if (err instanceof AssetRequestError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err.message : t('common.somethingWrong'));
        }
      } finally {
        setLoading(false);
        setListEditBusyId(null);
      }
    },
    [createFacilitiesCatalog, t],
  );

  const onBeginUpdateFromListDetail = useCallback(async () => {
    if (!canMutateAssets || listDetailAsset === null) {
      return;
    }
    if (!canMutateAssetAsSessionOwner(listDetailAsset, getSessionToken())) {
      setError(t('assets.updateOwnerOnly'));
      return;
    }
    const idRaw = assetRecordApiId(listDetailAsset);
    if (!idRaw) {
      setError(t('assets.noAssetId'));
      return;
    }
    await startAssetEditFromRecord(idRaw.trim());
  }, [canMutateAssets, listDetailAsset, startAssetEditFromRecord, t]);

  const onEditAssetFromList = useCallback(
    async (asset: Record<string, unknown>) => {
      if (!canMutateAssets) {
        return;
      }
      if (!canMutateAssetAsSessionOwner(asset, getSessionToken())) {
        setError(t('assets.updateOwnerOnly'));
        return;
      }
      const idRaw = assetRecordApiId(asset);
      if (!idRaw) {
        setError(t('assets.noAssetId'));
        return;
      }
      await startAssetEditFromRecord(idRaw.trim());
    },
    [canMutateAssets, startAssetEditFromRecord, t],
  );

  const onDeleteDisplayedAsset = useCallback(async () => {
    if (!canMutateAssets || operation !== 'getById') {
      return;
    }
    const id = assetId.trim();
    if (!id) {
      setError(t('assets.deleteIdRequired'));
      return;
    }
    if (!unifiedDeleteEnabled) {
      return;
    }
    const deleteLabel =
      displayedPlainForOwnerGate !== null
        ? assetRecordDisplayTitle(displayedPlainForOwnerGate).trim() || id
        : id;
    if (!window.confirm(`Delete asset “${deleteLabel}”? This cannot be undone.`)) {
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const idKey = id.toLowerCase();
      const delData = deletedListAssetIds.has(idKey)
        ? null
        : await deleteAssetByIdAllowNotFound(id);
      setDisplayReadyId(null);
      setUpdateEditReady(false);
      setUpdateLoadedForId(null);
      setAssetBody(emptyAssetUpdateBody());
      clearUpdateImageFiles();
      assetEditorBaselineRecordRef.current = null;
      setResponse({ operation: 'delete', data: delData });
      setDeletedListAssetIds((prev) => {
        const next = new Set(prev);
        next.add(idKey);
        return next;
      });
      clearLastWorkedAssetIdIfStoredIs(normalizeAssetOpId(id));
    } catch (err) {
      if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(err instanceof Error ? err.message : t('common.somethingWrong'));
      }
    } finally {
      setLoading(false);
    }
  }, [
    assetId,
    canMutateAssets,
    deletedListAssetIds,
    displayedPlainForOwnerGate,
    operation,
    unifiedDeleteEnabled,
  ]);

  const onOperationChange = (next: Operation) => {
    setOperation(next);
    setError(null);
    setResponse(null);
    setListDetailAsset(null);
    setDisplayReadyId(null);
    setUpdateSuccessAssetId(null);
    setUpdateEditReady(false);
    setUpdateLoadedForId(null);
    setDeletedListAssetIds(new Set());
    assetEditorBaselineRecordRef.current = null;
    if (next !== 'listMine' && next !== 'active') {
      setListFilterCountryId('');
      setListFilterDistrictId('');
      setListFilterAssetTypeId('');
      setListNameSearch('');
      setListFilterRegions([]);
      setListFilterRegionsStatus('idle');
      setListFilterRegionsError(null);
    }
    if (next === 'create') {
      loadCreateFormFromDraft(ownerLoginId);
    } else if (next === 'getById') {
      setAssetBody(emptyAssetUpdateBody());
      const last = readLastWorkedAssetId();
      if (last) {
        setAssetId(last);
      }
    }
  };

  const onAssetFieldChange = useCallback((path: string[], value: unknown) => {
    setAssetBody((prev) => {
      let next = setAtPath(prev, path, value);
      if (path.length === 1 && path[0] === 'country_id') {
        const prevCountry = prev.country_id;
        const countryChanged =
          prevCountry !== value &&
          String(prevCountry ?? '').trim() !== String(value ?? '').trim();
        if (countryChanged) {
          next = setAtPath(next, ['state_id'], null);
        }
      }
      return coerceAssetBodyOpeningTimes(next);
    });
  }, []);

  const onClearListFilters = useCallback(() => {
    setListFilterCountryId(
      assetsBrowseParams.countryId != null ? String(assetsBrowseParams.countryId) : '',
    );
    setListFilterDistrictId('');
    const typeFromId =
      assetsBrowseParams.assetTypeId != null ? String(assetsBrowseParams.assetTypeId) : '';
    const typeFromLabel = assetsBrowseParams.assetTypeLabel
      ? matchBrowseFilterTypeId(assetsBrowseParams.assetTypeLabel, browseFilterAssetTypeChoices)
      : null;
    setListFilterAssetTypeId(typeFromId || (typeFromLabel != null ? String(typeFromLabel) : ''));
    setListNameSearch(assetsBrowseParams.listSearch?.trim() ?? '');
    setListFilterRegions([]);
    setListFilterRegionsStatus('idle');
    setListFilterRegionsError(null);
  }, [
    assetsBrowseParams.assetTypeId,
    assetsBrowseParams.assetTypeLabel,
    assetsBrowseParams.countryId,
    assetsBrowseParams.listSearch,
    browseFilterAssetTypeChoices,
  ]);

  const runActiveListingFetch = useCallback(async () => {
    if (!canMutateAssets) {
      clientBrowseSearch.refresh();
      return;
    }
    setError(null);
    setResponse(null);
    setListDetailAsset(null);
    setLoading(true);
    try {
      const data = await fetchAssetListing('active', false);
      setResponse({ operation: 'active', data });
      activeListingReadyRef.current = true;
    } catch (err) {
      activeListingReadyRef.current = false;
      if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(err instanceof Error ? err.message : t('common.somethingWrong'));
      }
    } finally {
      setLoading(false);
    }
  }, [canMutateAssets, clientBrowseSearch.refresh, t]);

  const runOwnerListingFetch = useCallback(async () => {
    const gen = ++ownerListingFetchGenRef.current;
    setError(null);
    setListDetailAsset(null);
    setLoading(true);
    try {
      const data = await fetchAssetListing('listMine', false);
      if (gen !== ownerListingFetchGenRef.current) {
        return;
      }
      setResponse({ operation: 'listMine', data });
      setDeletedListAssetIds((prev) => pruneDeletedListAssetIdsForPayload(prev, data));
    } catch (err) {
      if (gen !== ownerListingFetchGenRef.current) {
        return;
      }
      if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(err instanceof Error ? err.message : t('common.somethingWrong'));
      }
    } finally {
      if (gen === ownerListingFetchGenRef.current) {
        setLoading(false);
      }
    }
  }, [t]);

  const onOwnerEmbeddedMode = useCallback(
    (mode: 'listMine' | 'create') => {
      setError(null);
      setListDetailAsset(null);
      if (mode === 'listMine') {
        setOperation('listMine');
        void runOwnerListingFetch();
        return;
      }
      setOperation('create');
      setResponse(null);
      loadCreateFormFromDraft(ownerLoginId);
      setFormEpoch((k) => k + 1);
    },
    [runOwnerListingFetch, ownerLoginId, loadCreateFormFromDraft],
  );

  const showAssetsListFromTab = useCallback(() => {
    if (assetsBrowseParams.assetId?.trim()) {
      return;
    }
    activeListingReadyRef.current = false;
    listDetailFetchGenRef.current += 1;
    setUpdateEditReady(false);
    setUpdateLoadedForId(null);
    setDisplayReadyId(null);
    assetEditorBaselineRecordRef.current = null;
    clearUpdateImageFiles();
    setAssetBody(emptyAssetUpdateBody());
    setFormEpoch((k) => k + 1);
    setListDetailAsset(null);
    setResponse(null);
    setError(null);
    if (canMutateAssets && !assetsBrowseParams.catalogBrowse) {
      setOperation('listMine');
      void runOwnerListingFetch();
      return;
    }
    setOperation('active');
    clientBrowseSearch.refresh();
  }, [
    assetsBrowseParams.assetId,
    assetsBrowseParams.catalogBrowse,
    canMutateAssets,
    clientBrowseSearch.refresh,
    runOwnerListingFetch,
  ]);

  const leaveAssetEditToList = useCallback(() => {
    activeListingReadyRef.current = false;
    listDetailFetchGenRef.current += 1;
    onDiscardAssetEdit();
    setDisplayReadyId(null);
    setListDetailAsset(null);
    setResponse(null);
    setError(null);
    if (canMutateAssets && !assetsBrowseParams.catalogBrowse) {
      setOperation('listMine');
      void runOwnerListingFetch();
      return;
    }
    setOperation('active');
    clientBrowseSearch.refresh();
  }, [
    assetsBrowseParams.catalogBrowse,
    canMutateAssets,
    clientBrowseSearch.refresh,
    onDiscardAssetEdit,
    runOwnerListingFetch,
  ]);

  useEffect(() => {
    if (!embedded || !pageTheme || assetsTabFocus < 1) {
      return;
    }
    if (assetsTabFocusSeenRef.current === null) {
      assetsTabFocusSeenRef.current = assetsTabFocus;
      return;
    }
    if (assetsTabFocusSeenRef.current === assetsTabFocus) {
      return;
    }
    assetsTabFocusSeenRef.current = assetsTabFocus;
    showAssetsListFromTab();
  }, [embedded, pageTheme, assetsTabFocus, showAssetsListFromTab]);

  useEffect(() => {
    if (!embedded || !pageTheme) {
      return;
    }
    setOpen(true);
    setListFilterCountryId(
      assetsBrowseParams.countryId != null ? String(assetsBrowseParams.countryId) : '',
    );
    setListFilterDistrictId('');
    setListNameSearch(assetsBrowseParams.listSearch?.trim() ?? '');
    const typeFromId =
      assetsBrowseParams.assetTypeId != null ? String(assetsBrowseParams.assetTypeId) : '';
    const typeFromLabel = assetsBrowseParams.assetTypeLabel
      ? matchBrowseFilterTypeId(assetsBrowseParams.assetTypeLabel, [])
      : null;
    setListFilterAssetTypeId(typeFromId || (typeFromLabel != null ? String(typeFromLabel) : ''));
    setListFilterRegions([]);
    setListFilterRegionsStatus('idle');
    setListFilterRegionsError(null);
    if (!assetsBrowseParams.assetId?.trim()) {
      setListDetailAsset(null);
    }
    if (canMutateAssets && !assetsBrowseParams.catalogBrowse) {
      setOperation('listMine');
      void runOwnerListingFetch();
      return;
    }
    setOperation('active');
  }, [
    embedded,
    pageTheme,
    assetsBrowseParams.assetId,
    assetsBrowseParams.assetTypeId,
    assetsBrowseParams.assetTypeLabel,
    assetsBrowseParams.catalogBrowse,
    assetsBrowseParams.listSearch,
    assetsBrowseParams.countryId,
    canMutateAssets,
    runOwnerListingFetch,
  ]);

  useEffect(() => {
    const label = assetsBrowseParams.assetTypeLabel?.trim();
    if (!embedded || !pageTheme || !label || browseFilterAssetTypeChoices.length === 0) {
      return;
    }
    const realId = matchBrowseFilterTypeId(label, browseFilterAssetTypeChoices);
    if (realId == null || realId >= 9000) {
      return;
    }
    setListFilterAssetTypeId((current) => {
      if (current === String(realId)) {
        return current;
      }
      const n = Number(current);
      if (current === '' || (Number.isFinite(n) && n >= 9000)) {
        return String(realId);
      }
      return current;
    });
  }, [
    embedded,
    pageTheme,
    assetsBrowseParams.assetTypeLabel,
    browseFilterAssetTypeChoices,
  ]);

  useEffect(() => {
    const want = assetsBrowseParams.countryId;
    if (!embedded || !pageTheme || want == null || countries.length === 0) {
      return;
    }
    const exact = countries.find((item) => Number(item.id) === want);
    const israelName =
      want === ISRAEL_COUNTRY_ID
        ? countries.find((item) => /israel|ישראל/i.test(String(item.label ?? '')))
        : null;
    const matched = exact ?? israelName;
    if (!matched || !Number.isFinite(Number(matched.id))) {
      return;
    }
    const nextId = String(matched.id);
    setListFilterCountryId((current) => {
      if (current === nextId) {
        return current;
      }
      if (current === '' || current === String(want)) {
        return nextId;
      }
      return current;
    });
  }, [embedded, pageTheme, assetsBrowseParams.countryId, countries]);

  const openAssetIdFetchRef = useRef<string | null>(null);

  useEffect(() => {
    if (!embedded || !pageTheme) {
      return;
    }
    const wantId = assetsBrowseParams.assetId?.trim();
    if (!wantId) {
      openAssetIdFetchRef.current = null;
      return;
    }

    const normalized = normalizeAssetOpId(wantId);
    if (
      urlSyncedAssetIdRef.current &&
      urlSyncedAssetIdRef.current !== normalized
    ) {
      urlSyncedAssetIdRef.current = null;
    }
    const detailMatchesWant =
      listDetailAsset != null && assetRecordMatchesDisplayId(listDetailAsset, wantId);
    if (urlSyncedAssetIdRef.current === normalized && detailMatchesWant) {
      return;
    }

    const inList = displayListRecords.find((a) => assetRecordMatchesDisplayId(a, wantId));
    if (inList) {
      urlSyncedAssetIdRef.current = normalized;
      enrichListDetailFromApi(normalized, inList);
      openAssetIdFetchRef.current = null;
      return;
    }

    const listSnapshot = browseAssetListSnapshotForId(wantId);
    if (listSnapshot) {
      urlSyncedAssetIdRef.current = normalized;
      const snapshotId = assetRecordPrimaryId(listSnapshot) ?? wantId;
      enrichListDetailFromApi(snapshotId, listSnapshot);
      openAssetIdFetchRef.current = null;
      return;
    }

    if (openAssetIdFetchRef.current === normalized) {
      return;
    }
    openAssetIdFetchRef.current = normalized;

    let cancelled = false;
    void (async () => {
      try {
        const loaded = await getAssetByIdBestEffort(
          listSnapshot ?? { id: wantId, asset_id: wantId },
          wantId,
        );
        if (cancelled || !isPlainObject(loaded)) {
          return;
        }
        if (normalizeAssetOpId(assetsBrowseParams.assetId ?? '') !== normalized) {
          return;
        }
        urlSyncedAssetIdRef.current = normalized;
        const snapshot = browseAssetListSnapshotForId(wantId);
        const merged = snapshot
          ? mergeAssetApiRecordWithListSnapshot(loaded, snapshot)
          : loaded;
        setListDetailAsset(prepareFavoriteAssetSnapshot(merged));
      } catch (err) {
        if (cancelled || normalizeAssetOpId(assetsBrowseParams.assetId ?? '') !== normalized) {
          return;
        }
        if (isAssetShortDisplayId(wantId) && !browseAssetListSnapshotForId(wantId)) {
          setError(
            err instanceof AssetShortIdNotFoundError
              ? t('assets.shortIdNotInCatalog', { id: err.shortId })
              : t('assets.shortIdNotInCatalog', { id: wantId.trim() }),
          );
          setListDetailAsset(null);
          return;
        }
        const fallback = browseAssetListSnapshotForId(wantId);
        if (fallback) {
          urlSyncedAssetIdRef.current = normalized;
          setListDetailAsset(prepareFavoriteAssetSnapshot(fallback));
        }
      } finally {
        if (openAssetIdFetchRef.current === normalized) {
          openAssetIdFetchRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      if (openAssetIdFetchRef.current === normalized) {
        openAssetIdFetchRef.current = null;
      }
    };
  }, [
    embedded,
    pageTheme,
    assetsBrowseParams.assetId,
    listDetailAsset,
    enrichListDetailFromApi,
    displayListRecordIdsKey,
    t,
  ]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const keepPanelDuringAssetUpdate = operation === 'getById' && updateEditReady;
    if (!keepPanelDuringAssetUpdate) {
      setResponse(null);
    }
    setListDetailAsset(null);
    setLoading(true);
    try {
      let data: unknown;
      let shouldSetResponse = true;
      let panelOperation: AssetPanelResponseOperation = operation;
      const id = assetId.trim();

      switch (operation) {
        case 'getById': {
          if (!id) {
            throw new Error(t('assets.idRequired'));
          }
          if (updateEditReady) {
            if (!canMutateAssets) {
              throw new Error(t('assets.updateNotForAccount'));
            }
            const resolvedAssetBody = applyAssetMutationSubmitDefaults(
              await resolveSubmitAssetBody(),
              regions,
              'update',
            );
            assertAssetUpdateBodyReady(resolvedAssetBody, regions, t);
            if (assetBodyHasInvalidPricingPrices(resolvedAssetBody)) {
              throw new Error(t('assets.pricingPriceRequired'));
            }
            const pathsUpdate = normalizeAssetImagesToWireStrings(resolvedAssetBody.images);
            let filesAlignedUpdate = alignParallelImageFiles(updateImageFilesRef.current, pathsUpdate.length);

            const imageUrlStringsPatch: string[] = [];
            const filesToUploadPatch: File[] = [];
            for (let i = 0; i < pathsUpdate.length; i++) {
              const f = filesAlignedUpdate[i];
              if (f) {
                filesToUploadPatch.push(f);
              } else {
                imageUrlStringsPatch.push(pathsUpdate[i]);
              }
            }

            const compressedPatchFiles = await prepareImageFilesForMultipartUpload(
              filesToUploadPatch,
              assetImageLimitMessages,
            );

            const multipartUpdate = compressedPatchFiles.length > 0;
            const baselineRecord = assetEditorBaselineRecordRef.current;
            if (multipartUpdate) {
              const draftWithImages = { ...resolvedAssetBody, images: imageUrlStringsPatch };
              const jsonSource = ASSET_PATCH_MULTIPART_JSON_MINIMAL
                ? buildAssetPatchDelta(baselineRecord, draftWithImages)
                : draftWithImages;
              const patchPayload = sanitizeAssetPatchRequestBody(jsonSource, {
                preserveEmptyWireImagesForMultipartFiles: true,
                countryId: resolvedAssetBody.country_id ?? baselineRecord?.country_id,
              });
              if (Object.keys(patchPayload).length === 0) {
                patchPayload.images = imageUrlStringsPatch.length > 0 ? imageUrlStringsPatch : [];
              }
              const patchJsonBytes = estimateAssetPatchBodyBytes(patchPayload);
              if (patchJsonBytes > 95_000) {
                throw new Error(
                  `PATCH JSON part is ${Math.round(patchJsonBytes / 1024)} KB; this API often rejects bodies over ~100 KB. ` +
                    'Try changing fewer fields at once, or set REACT_APP_ASSET_PATCH_MULTIPART_JSON_MINIMAL=true (dev default) so only changed fields are sent.',
                );
              }
              const fd = buildAssetCreateMultipartFormData({
                preparedPayload: patchPayload,
                imageFiles: compressedPatchFiles,
              });
              data = await updateAssetWithMultipartFormData(id, fd);
            } else {
              const patchBody = buildAssetPatchDelta(baselineRecord, resolvedAssetBody);
              if (Object.keys(patchBody).length === 0) {
                throw new Error(t('assets.noChangesToSave'));
              }
              const patchJsonBytes = estimateAssetPatchBodyBytes(patchBody);
              if (patchJsonBytes > 95_000) {
                throw new Error(
                  `PATCH body is ${Math.round(patchJsonBytes / 1024)} KB; this API often rejects JSON over ~100 KB. ` +
                    'Change fewer fields per update, or ask the backend team to raise the body-size limit.',
                );
              }
              data = await requestAssetApi({
                method: 'PATCH',
                path: `/assets/${encodeURIComponent(id)}`,
                body: patchBody,
              });
            }
            panelOperation = 'getById';
            const refreshed = await fetchFullAssetPayloadAfterSuccessfulPatch(id);
            if (isPlainObject(refreshed)) {
              data = await enrichAssetRecordAddressForDisplayWithGeocode(
                refreshed,
                assetGeoLabels,
                resolvedAssetBody,
                baselineRecord,
              );
            } else {
              data = mergeAssetDisplayPayloadAfterPatch(data, resolvedAssetBody, baselineRecord, {
                skipImagesEditorOverlay: multipartUpdate,
              });
              if (isPlainObject(data)) {
                data = await enrichAssetRecordAddressForDisplayWithGeocode(
                  data,
                  assetGeoLabels,
                  resolvedAssetBody,
                  baselineRecord,
                );
              }
            }
            const wantBookable = assetRecordIsBookable(resolvedAssetBody);
            if (isPlainObject(data) && assetRecordIsBookable(data) !== wantBookable) {
              try {
                const persisted = await persistAssetBookingAvailability({
                  assetId: id,
                  available: wantBookable,
                  requestPatch: async (assetId, body) => {
                    await requestAssetApi({
                      method: 'PATCH',
                      path: `/assets/${encodeURIComponent(assetId)}`,
                      body,
                    });
                  },
                  loadAsset: async (assetId) => {
                    const loaded = await getAssetById(assetId);
                    if (!isPlainObject(loaded)) {
                      throw new Error(t('common.somethingWrong'));
                    }
                    return loaded;
                  },
                });
                data = {
                  ...data,
                  ...persisted.asset,
                  asset_status: persisted.status,
                };
              } catch (availabilityErr) {
                if (availabilityErr instanceof AssetBookingAvailabilityPersistError) {
                  throw new Error(
                    t('assets.availabilityNotPersisted', {
                      tried: availabilityErr.triedStatuses.join(', '),
                    }),
                  );
                }
                throw availabilityErr;
              }
            }
            if (isPlainObject(data)) {
              const previewSrcs = updateImagePreviewSrcsRef.current;
              let displayRecord = applyUploadedImagePreviewsToAssetRecord(
                data,
                pathsUpdate,
                filesAlignedUpdate,
                previewSrcs,
              );
              if (assetRecordResolvedImageSrcs(displayRecord).length === 0) {
                const fallbackImages = buildAssetUpdateDisplayImages(
                  pathsUpdate,
                  filesAlignedUpdate,
                  previewSrcs,
                );
                if (fallbackImages.length > 0) {
                  displayRecord = { ...displayRecord, images: fallbackImages };
                }
              }
              rememberBrowseAssetListSnapshot(displayRecord);
              data = displayRecord;
            }
            invalidateAssetThumbnailCacheForId(id);
            invalidateBookingAssetThumbnailSearchCache();
            if (isPlainObject(data)) {
              registerAssetThumbnailRecords([data]);
            }
            assetEditorBaselineRecordRef.current = null;
            setDisplayReadyId(normalizeAssetOpId(id));
          } else {
            setDisplayReadyId(null);
            const loaded = await getAssetById(id);
            data = isPlainObject(loaded)
              ? await enrichAssetRecordAddressForDisplayWithGeocode(loaded, assetGeoLabels)
              : loaded;
          }
          break;
        }
        case 'listMine':
          data = await fetchAssetListing('listMine', false);
          break;
        case 'active':
          if (!canMutateAssets) {
            data = (await fetchSearchAssetsForUiState(clientBrowseUiState)).records;
          } else {
            data = await fetchAssetListing('active', false);
          }
          break;
        case 'create': {
          if (!canMutateAssets) {
            throw new Error(t('assets.createNotForClients'));
          }
          if (!createTermsAgreed) {
            throw new Error(t('platform.termsRequired'));
          }
          const resolvedAssetBody = applyAssetCreateContactDefaults(
            applyAssetCreateSubmitDefaults(await resolveSubmitAssetBody(), regions),
          );
          assertAssetCreateBodyReady(resolvedAssetBody, regions, t);
          if (assetBodyHasInvalidPricingPrices(resolvedAssetBody)) {
            throw new Error(t('assets.pricingPriceRequired'));
          }
          persistAssetCreateDraft(deepClone(resolvedAssetBody), ownerLoginId);
          const paths = normalizedCreateImagesPaths(resolvedAssetBody.images);
          let filesAligned = alignParallelImageFiles(createImageFilesRef.current, paths.length);

          let filesToUpload: File[];
          let imageUrlStrings: string[];
          try {
            ({ filesToUpload, imageUrlStrings } = planCreateAssetImageUpload(paths, filesAligned));
          } catch (e) {
            if (e instanceof CreateAssetImageRepickRequiredError) {
              throw new Error(t('assets.imagesRePickRequired'));
            }
            throw e;
          }

          if (imageUrlStrings.length === 0 && filesToUpload.length === 0) {
            throw new Error(t('assets.imagesAtLeastOne'));
          }

          const registrationDate = new Date().toISOString();
          const basePayload = {
            ...resolvedAssetBody,
            registration_date: registrationDate,
            images: imageUrlStrings,
          };

          const compressedCreateFiles = await prepareImageFilesForMultipartUpload(
            filesToUpload,
            assetImageLimitMessages,
          );

          if (compressedCreateFiles.length > 0) {
            const prepared = prepareAssetRequestBody(basePayload);
            if (!isPlainObject(prepared)) {
              throw new Error(t('assets.createPayloadFailed'));
            }
            const fd = buildAssetCreateMultipartFormData({
              preparedPayload: prepared as Record<string, unknown>,
              imageFiles: compressedCreateFiles,
            });
            data = await createAssetWithMultipartFormData(fd);
          } else {
            data = await createAsset(basePayload);
          }
          {
            const createdId = extractAssetIdentifierFromApiPayload(data);
            if (createdId !== null) {
              setAssetId(createdId);
              persistLastWorkedAssetId(createdId);
            }
          }
          break;
        }
        default: {
          const _n: never = operation;
          throw new Error(String(_n));
        }
      }

      if (shouldSetResponse) {
        if (operation === 'getById' && id) {
          if (updateEditReady) {
            setUpdateEditReady(false);
            setUpdateLoadedForId(null);
            setAssetBody(emptyAssetUpdateBody());
            clearUpdateImageFiles();
            setDisplayReadyId(normalizeAssetOpId(id));
            setUpdateSuccessAssetId(normalizeAssetOpId(id));
            setFormEpoch((k) => k + 1);
          } else {
            setAssetId(id);
            setDisplayReadyId(normalizeAssetOpId(id));
            setUpdateSuccessAssetId(null);
          }
          persistLastWorkedAssetId(id);
        }
        setResponse({ operation: panelOperation, data });
        if (operation === 'create') {
          setUpdateSuccessAssetId(null);
          clearCreateImageFiles();
          clearPersistedAssetCreateImagePreviews(ownerLoginId);
          createImageDraftHydratedRef.current = false;
          setCreateTermsOpen(false);
          setCreateTermsAgreed(false);
          setAssetBody((prev) => ({
            ...ensureAssetCreateFormScalars(prev),
            images: [],
          }));
        }
        if (operation === 'listMine' || operation === 'active') {
          setDeletedListAssetIds((prev) => pruneDeletedListAssetIdsForPayload(prev, data));
        }
      }
    } catch (err) {
      if (err instanceof AssetShortIdNotFoundError) {
        setError(t('assets.shortIdNotInCatalog', { id: err.shortId }));
      } else if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(err instanceof Error ? err.message : t('common.somethingWrong'));
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentAssetList = useCallback(async () => {
    if (clientBrowseActive) {
      clientBrowseSearch.refresh();
      return;
    }
    const data =
      operation === 'listMine'
        ? await fetchAssetListing('listMine', false)
        : await fetchAssetListing('active', false);
    setResponse({ operation, data });
    setDeletedListAssetIds((prev) => pruneDeletedListAssetIdsForPayload(prev, data));
  }, [clientBrowseActive, clientBrowseSearch.refresh, operation]);

  const onDeleteAssetFromList = useCallback(
    async (asset: Record<string, unknown>) => {
      if (!canMutateAssets) {
        return;
      }
      if (!canMutateAssetAsSessionOwner(asset, getSessionToken())) {
        setError('Delete is only available for assets you own.');
        return;
      }
      const delId = assetRecordApiId(asset);
      if (!delId) {
        setError('This row has no asset id — cannot delete.');
        return;
      }
      const title = assetRecordDisplayTitle(asset);
      if (!window.confirm(`Delete asset “${title}”? This cannot be undone.`)) {
        return;
      }
      setError(null);
      setListDeleteBusyId(delId);
      try {
        await deleteAssetByIdAllowNotFound(delId);
        const idKey = delId.toLowerCase();
        clearLastWorkedAssetIdIfStoredIs(normalizeAssetOpId(delId));
        setDeletedListAssetIds((prev) => {
          const next = new Set(prev);
          next.add(idKey);
          return next;
        });
        setListDetailAsset((prev) => {
          if (!prev) {
            return null;
          }
          return canonicalListAssetRowId(prev) === idKey ? null : prev;
        });
        await refreshCurrentAssetList();
      } catch (err) {
        if (err instanceof AssetRequestError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err.message : 'Delete failed.');
        }
      } finally {
        setListDeleteBusyId(null);
      }
    },
    [canMutateAssets, refreshCurrentAssetList],
  );

  const onToggleBookingAvailabilityFromList = useCallback(
    async (asset: Record<string, unknown>, available: boolean) => {
      if (!canMutateAssets) {
        return;
      }
      if (!canMutateAssetAsSessionOwner(asset, getSessionToken())) {
        setError(t('assets.updateOwnerOnly'));
        return;
      }
      const id = assetRecordApiId(asset);
      if (!id) {
        setError(t('assets.noAssetId'));
        return;
      }
      const idKey = id.toLowerCase();
      setError(null);
      setListAvailabilityBusyId(id);
      const previousResponse = response;
      const optimisticStatus = available ? 'active' : 'inactive';
      setResponse((prev) => {
        if (!prev || prev.operation !== 'listMine') {
          return prev;
        }
        return {
          ...prev,
          data: patchAssetStatusInListPayload(prev.data, idKey, optimisticStatus),
        };
      });
      setListDetailAsset((prev) => {
        if (!prev) {
          return prev;
        }
        return canonicalListAssetRowId(prev) === idKey
          ? { ...prev, asset_status: optimisticStatus }
          : prev;
      });
      try {
        const persisted = await persistAssetBookingAvailability({
          assetId: id,
          available,
          requestPatch: async (assetId, body) => {
            await requestAssetApi({
              method: 'PATCH',
              path: `/assets/${encodeURIComponent(assetId)}`,
              body,
            });
          },
          loadAsset: async (assetId) => {
            const loaded = await getAssetById(assetId);
            if (!isPlainObject(loaded)) {
              throw new Error(t('common.somethingWrong'));
            }
            return loaded;
          },
        });
        setResponse((prev) => {
          if (!prev || prev.operation !== 'listMine') {
            return prev;
          }
          return {
            ...prev,
            data: patchAssetStatusInListPayload(prev.data, idKey, persisted.status),
          };
        });
        setListDetailAsset((prev) => {
          if (!prev) {
            return prev;
          }
          return canonicalListAssetRowId(prev) === idKey
            ? { ...prev, ...persisted.asset, asset_status: persisted.status }
            : prev;
        });
      } catch (err) {
        setResponse(previousResponse);
        if (err instanceof AssetBookingAvailabilityPersistError) {
          setError(
            t('assets.availabilityNotPersisted', {
              tried: err.triedStatuses.join(', '),
            }),
          );
        } else if (err instanceof AssetRequestError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err.message : t('common.somethingWrong'));
        }
      } finally {
        setListAvailabilityBusyId(null);
      }
    },
    [canMutateAssets, response, t],
  );

  const createSuccessAssetId = useMemo(() => {
    if (!response || response.operation !== 'create') {
      return null;
    }
    const fromPayload = extractAssetIdentifierFromApiPayload(response.data);
    if (fromPayload) {
      return fromPayload;
    }
    const trimmed = assetId.trim();
    return trimmed || null;
  }, [response, assetId]);

  useEffect(() => {
    if (!createSuccessAssetId) {
      return;
    }
    return scheduleScrollPanelToElement(panelScrollRef, createSuccessNoticeRef);
  }, [createSuccessAssetId]);

  const isBrowsePage = embedded && pageTheme;

  const showUpdateSuccessNotice = useMemo(() => {
    if (!updateSuccessAssetId || assetEditing) {
      return false;
    }
    if (response?.operation === 'getById' && canRenderAssetGetByIdView(response.data)) {
      const id = extractAssetIdentifierFromApiPayload(response.data) ?? displayReadyId;
      return id !== null && normalizeAssetOpId(id) === updateSuccessAssetId;
    }
    if (listDetailAsset) {
      const id = assetRecordApiId(listDetailAsset);
      return id !== null && normalizeAssetOpId(id) === updateSuccessAssetId;
    }
    return false;
  }, [updateSuccessAssetId, assetEditing, response, displayReadyId, listDetailAsset]);

  useEffect(() => {
    if (!showUpdateSuccessNotice) {
      return;
    }
    return scheduleScrollPanelToElement(panelScrollRef, updateSuccessNoticeRef);
  }, [showUpdateSuccessNotice, updateSuccessAssetId]);

  useEffect(() => {
    const message =
      typeof error === 'string'
        ? error
        : clientBrowseActive && clientBrowseSearch.error
          ? clientBrowseSearch.error
          : null;
    if (!message) {
      return;
    }
    return scheduleScrollPanelToElement(panelScrollRef, panelErrorNoticeRef);
  }, [error, clientBrowseActive, clientBrowseSearch.error]);

  const updateSuccessBanner =
    showUpdateSuccessNotice && updateSuccessAssetId ? (
      <p
        ref={updateSuccessNoticeRef}
        tabIndex={-1}
        className={isBrowsePage ? pageThemeStyles.browseCreateSuccess : styles.createSuccess}
        role="status"
      >
        {t('assets.updateSuccess', { id: formatAssetShortId(updateSuccessAssetId) })}
      </p>
    ) : null;

  const showBookFromActiveList =
    operation === 'active' &&
    !canMutateAssets &&
    (clientBrowseActive || (response !== null && response.operation === 'active'));
  const bookButtonsDisabled = !canBookAssets(userLoginType);
  const bookDisabledTitle = !isLoggedIn
    ? t('assets.bookSignIn')
    : !canBookAssets(userLoginType)
      ? isBusinessClientLogin(userLoginType)
        ? t('bookings.error.roleBlock.businessClient')
        : t('bookings.error.roleBlock.loginType')
      : undefined;
  const inlineQuickBookAsset = useMemo(() => {
    if (!pendingAssetQuickBook) {
      return null;
    }
    const wantId = pendingAssetQuickBook.assetId;
    if (listDetailAsset) {
      const id = assetRecordPrimaryId(listDetailAsset);
      if (id === wantId) {
        return pendingAssetQuickBook.assetSnapshot;
      }
    }
    if (
      response?.operation === 'getById' &&
      isPlainObject(response.data) &&
      canRenderAssetGetByIdView(response.data)
    ) {
      const id = assetRecordPrimaryId(response.data);
      if (id === wantId) {
        return response.data as Record<string, unknown>;
      }
    }
    return null;
  }, [pendingAssetQuickBook, listDetailAsset, response]);
  const showInlineAssetQuickBook = inlineQuickBookAsset !== null;
  const inlineQuickBookCurrency = useMemo(
    () =>
      inlineQuickBookAsset
        ? resolveAssetLocaleCurrency(inlineQuickBookAsset)
        : resolveAssetLocaleCurrency({}),
    [inlineQuickBookAsset],
  );
  const useAssetProfileLayout = isBrowsePage || showBookFromActiveList;
  const hideBrowseListFilters =
    isBrowsePage &&
    (assetEditing ||
      listDetailAsset !== null ||
      Boolean(assetsBrowseParams.assetId?.trim()));
  const showingAssetEditPage = isBrowsePage && assetEditing && canMutateAssets;
  const showingAssetDetailOnBrowse =
    isBrowsePage &&
    !assetEditing &&
    (hideBrowseListFilters ||
      (response?.operation === 'getById' &&
        canRenderAssetGetByIdView(response.data)));
  /** Owner "my assets" list on the browse page uses the same multi-column grid as client browse. */
  const ownerAssetListGridOnBrowse =
    isBrowsePage &&
    !clientBrowseActive &&
    !showingAssetEditPage &&
    !showingAssetDetailOnBrowse &&
    response !== null &&
    isListLikePanelOperation(response.operation) &&
    listRecordsBase.length > 0;

  const browseDetailBackLabel = useMemo(() => {
    if (assetEditing) {
      return t('assets.backToList');
    }
    if (assetsBrowseParams.returnView === 'search') {
      return t('assets.backToSearch');
    }
    if (assetsBrowseParams.returnView === 'favorites') {
      return t('favorites.backToList');
    }
    if (assetsBrowseParams.returnView === 'home') {
      return t('nav.home');
    }
    return t('assets.backToList');
  }, [assetEditing, assetsBrowseParams.returnView, t]);
  const onBrowseAssetBack = useCallback(() => {
    if (assetEditing) {
      leaveAssetEditToList();
      return;
    }
    if (
      canMutateAssets &&
      response?.operation === 'getById' &&
      !listDetailAsset &&
      !assetsBrowseParams.assetId?.trim()
    ) {
      leaveAssetEditToList();
      return;
    }
    clearListDetailAsset();
  }, [
    assetEditing,
    assetsBrowseParams.assetId,
    canMutateAssets,
    clearListDetailAsset,
    leaveAssetEditToList,
    listDetailAsset,
    response,
  ]);
  const hideAssetsFormSubmit =
    assetEditing
      ? false
      : (embedded && pageTheme && isListLikeOperation(operation) && response !== null) ||
        (embedded && pageTheme && clientBrowseActive) ||
        showingAssetDetailOnBrowse;
  const activeListDetailAsset = shouldShowListDetailPanel(
    listDetailAsset,
    clientBrowseActive,
    response,
    assetsBrowseParams.assetId,
  )
    ? listDetailAsset
    : null;
  const displayedListDetailAsset = useMemo(() => {
    if (!activeListDetailAsset) {
      return null;
    }
    return enrichAssetRecordAddressForDisplay(activeListDetailAsset);
  }, [activeListDetailAsset]);
  const openingDeepLinkedAsset =
    isBrowsePage &&
    Boolean(assetsBrowseParams.assetId?.trim()) &&
    displayedListDetailAsset === null &&
    openAssetIdFetchRef.current !== null;
  const ownerEmbeddedPageTitle =
    isBrowsePage && canMutateAssets && !ownerCatalogBrowse
      ? assetEditing
        ? t('assets.editDisplayed')
        : operation === 'create'
          ? t('assets.operation.create')
          : null
      : t('assets.title');
  const filterGridClass = isBrowsePage ? pageThemeStyles.browseFiltersGrid : styles.listFiltersGrid;
  const filterFieldClass = isBrowsePage ? undefined : styles.listFilterField;
  const filterHeadingClass = isBrowsePage ? undefined : styles.listFilterHeading;
  const filterControlClass = isBrowsePage ? undefined : styles.listFilterControl;
  const formTheme = isBrowsePage ? ('light' as const) : ('dark' as const);
  const assetOwnerLogoOptions = useMemo(
    () =>
      sessionOwnerLogoSrc
        ? {
            sessionOwnerLogoSrc,
            sessionAccessToken: getSessionToken(),
          }
        : undefined,
    [sessionOwnerLogoSrc],
  );

  return (
    <div
      className={[
        styles.wrap,
        open && !embedded ? styles.wrapOpen : '',
        embedded ? pageThemeStyles.wrapEmbedded : '',
        pageTheme ? pageThemeStyles.wrapThemed : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={wrapRef}
    >
      {!embedded ? (
        <button
          type="button"
          className={styles.menuButton}
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          disabled={!assetsMenuAllowed}
          title={assetsMenuDisabledTitle}
          onClick={() => setOpen((v) => !v)}
        >
          {t('assets.title')}
        </button>
      ) : null}
      {open || embedded ? (
        <div
          id={menuId}
          ref={panelScrollRef}
          className={[
            styles.panel,
            useWideAssetPanel && !isBrowsePage ? styles.panelWide : '',
            embedded ? pageThemeStyles.panelEmbedded : '',
            isBrowsePage ? pageThemeStyles.browsePage : '',
            isBrowsePage && (clientBrowseActive || ownerAssetListGridOnBrowse)
              ? pageThemeStyles.browsePageWide
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-page-theme={isBrowsePage ? 'light' : undefined}
          role="region"
          aria-label={t('assets.apiLabel')}
        >
          {showingAssetDetailOnBrowse || showingAssetEditPage ? (
            <div className={isBrowsePage ? pageThemeStyles.pageHeader : styles.panelHeader}>
              <button
                type="button"
                className={styles.backToListButton}
                onClick={onBrowseAssetBack}
              >
                {browseDetailBackLabel}
              </button>
            </div>
          ) : ownerEmbeddedPageTitle ? (
            <div className={isBrowsePage ? pageThemeStyles.pageHeader : styles.panelHeader}>
              <span className={isBrowsePage ? pageThemeStyles.pageTitle : styles.panelTitle}>
                {ownerEmbeddedPageTitle}
              </span>
            </div>
          ) : null}
          {createSuccessAssetId ? (
            <p
              ref={createSuccessNoticeRef}
              tabIndex={-1}
              className={isBrowsePage ? pageThemeStyles.browseCreateSuccess : styles.createSuccess}
              role="status"
              aria-live="polite"
            >
              {t('assets.createSuccess', { id: formatAssetShortId(createSuccessAssetId) })}
            </p>
          ) : null}
          <form
            onSubmit={onSubmit}
            autoComplete="off"
            className={isBrowsePage ? pageThemeStyles.browseForm : undefined}
          >
            {embedded && pageTheme && canMutateAssets && !ownerCatalogBrowse && !assetEditing ? (
              <div
                className={pageThemeStyles.ownerModeTabs}
                role="tablist"
                aria-label={t('assets.ownerMode')}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={operation === 'listMine'}
                  className={[
                    pageThemeStyles.ownerModeTab,
                    operation === 'listMine' ? pageThemeStyles.ownerModeTabActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={loading}
                  onClick={() => onOwnerEmbeddedMode('listMine')}
                >
                  {t('assets.operation.listMine')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={operation === 'create'}
                  className={[
                    pageThemeStyles.ownerModeTab,
                    operation === 'create' ? pageThemeStyles.ownerModeTabActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={loading}
                  onClick={() => onOwnerEmbeddedMode('create')}
                >
                  {t('assets.operation.create')}
                </button>
              </div>
            ) : null}
            {embedded && pageTheme ? null : (
              <>
                <label className={styles.label} htmlFor={`${menuId}-operation`}>
                  {t('common.operation')}
                </label>
                <select
                  id={`${menuId}-operation`}
                  className={styles.operationSelect}
                  value={operation}
                  onChange={(ev) => onOperationChange(ev.target.value as Operation)}
                >
                  {operationChoices.map((op) => (
                    <option key={op.value} value={op.value}>
                      {assetOperationSelectLabel(op, canMutateAssets, t)}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* Asset type lives inside list filters and stays changeable after category navigation. */}
            {isListLikeOperation(operation) ? (
              isBrowsePage ? (
                <div className={pageThemeStyles.browseFiltersToolbar}>
                  {hideBrowseListFilters ? null : (
                    <details className={pageThemeStyles.browseFilters}>
                      <summary className={pageThemeStyles.browseFiltersSummary}>
                        {t('assets.filter.listFiltersTitle')}
                      </summary>
                      <div className={filterGridClass}>
                        {renderListFilterFields(
                          menuId,
                          filterFieldClass,
                          filterHeadingClass,
                          filterControlClass,
                          language,
                          t,
                          {
                            listFilterCountryId,
                            setListFilterCountryId,
                            setListFilterDistrictId,
                            countriesStatus,
                            countries,
                            countriesError,
                            listFilterDistrictId,
                            listFilterRegionsStatus,
                            listFilterRegions,
                            listFilterRegionsError,
                            listFilterAssetTypeId,
                            setListFilterAssetTypeId,
                            assetTypesStatus,
                            assetTypes: browseFilterAssetTypeChoices,
                            assetTypesError,
                            listNameSearch,
                            setListNameSearch,
                            listBrowseShowsRegionFilter,
                            listSortMode,
                            setListSortMode,
                          },
                        )}
                      </div>
                    </details>
                  )}
                  <AssistantSearchSlotChip text={assistantSlotChip} />
                  {clientBrowseActive &&
                  ((clientBrowseListView?.total ?? clientBrowseSearch.total) > 0 ||
                    clientBrowseSearch.catalogExpanding) ? (
                    <p className={pageThemeStyles.browseResultCount} role="status">
                      {clientBrowseSearch.catalogExpanding
                        ? t('assets.search.resultCountPartial', {
                            count: displayListRecords.length,
                          })
                        : t('assets.search.resultCount', {
                            count: displayListRecords.length,
                            total: clientBrowseListView?.total ?? clientBrowseSearch.total,
                          })}
                      {clientBrowseSearch.catalogExpanding ? (
                        <span className={pageThemeStyles.browseLoadingMore}>
                          {' '}
                          {t('assets.search.loadingMoreListings')}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              ) : (
                <details className={styles.listFiltersDetails}>
                  <summary className={styles.listFiltersSummary}>{t('assets.filter.listFilters')}</summary>
                  <div className={filterGridClass}>
                    {renderListFilterFields(
                      menuId,
                      filterFieldClass,
                      filterHeadingClass,
                      filterControlClass,
                      language,
                      t,
                      {
                        listFilterCountryId,
                        setListFilterCountryId,
                        setListFilterDistrictId,
                        countriesStatus,
                        countries,
                        countriesError,
                        listFilterDistrictId,
                        listFilterRegionsStatus,
                        listFilterRegions,
                        listFilterRegionsError,
                        listFilterAssetTypeId,
                        setListFilterAssetTypeId,
                        assetTypesStatus,
                        assetTypes: browseFilterAssetTypeChoices,
                        assetTypesError,
                        listNameSearch,
                        setListNameSearch,
                        listBrowseShowsRegionFilter,
                        listSortMode,
                        setListSortMode,
                      },
                    )}
                  </div>
                </details>
              )
            ) : null}

            {operationNeedsAssetId(operation) ? (
              <>
                <label className={styles.label} htmlFor={`${menuId}-asset-id`} style={{ marginTop: '0.65rem' }}>
                  {t('assetField.assetId')}
                </label>
                {showAssetIdAsShort ? (
                  <div className={styles.inputRow}>
                    <input
                      key={`${menuId}-asset-short-id-${formEpoch}`}
                      id={`${menuId}-asset-id`}
                      className={styles.input}
                      name="assetShortId"
                      value={
                        assetRecordShortDisplayId(assetBody) ??
                        formatAssetShortId(assetId) ??
                        ''
                      }
                      readOnly
                      disabled
                      title={assetId.trim() || undefined}
                      aria-label={t('assetField.assetId')}
                    />
                  </div>
                ) : (
                  <div className={styles.inputRow}>
                    <input
                      key={`${menuId}-asset-id-${formEpoch}`}
                      id={`${menuId}-asset-id`}
                      className={styles.input}
                      name="assetId"
                      value={assetId}
                      onChange={(ev) => setAssetId(ev.target.value)}
                      placeholder={t('assets.filter.placeholderAssetId')}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                )}
                {operation === 'getById' && canMutateAssets && !assetEditing ? (
                  <div className={styles.assetUnifiedActions} role="group" aria-label="After display actions">
                    {canPrepareAssetEdit ? (
                      <button
                        type="button"
                        className={styles.lastUsedUpdateIdUseButton}
                        onClick={() => void onPrepareAssetEdit()}
                        disabled={loading}
                      >
                        {t('assets.editDisplayed')}
                      </button>
                    ) : null}
                    {unifiedDeleteEnabled ? (
                      <button
                        type="button"
                        className={styles.submitDanger}
                        onClick={() => void onDeleteDisplayedAsset()}
                        disabled={loading}
                      >
                        {t('assets.deleteDisplayed')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {displayedAssetShowsOwnerMismatchHint && canMutateAssets ? (
                  <p className={styles.ownerGateHint}>
                    Edit and delete are only available when this asset’s owner id matches your session access token
                    (for example <code className={styles.ownerGateMono}>owner_id</code> on the asset and{' '}
                    <code className={styles.ownerGateMono}>user_id</code> or <code className={styles.ownerGateMono}>sub</code> in
                    the JWT).
                  </p>
                ) : null}
              </>
            ) : null}

            {showAssetMutationJsonFields(operation, assetEditing) ? (
              <div
                className={[
                  styles.bodyFormPanel,
                  operation === 'create' || assetEditing ? styles.bodyFormPanelExpanded : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {operation === 'getById' && assetEditing && canMutateAssets ? (
                  <div
                    className={styles.assetUnifiedActions}
                    role="group"
                    aria-label={t('assets.updateAsset')}
                    style={{ marginBottom: '0.75rem' }}
                  >
                    <button type="submit" className={styles.submit} disabled={loading}>
                      {loading ? '…' : t('common.update')}
                    </button>
                    <button
                      type="button"
                      className={styles.lastUsedUpdateIdUseButton}
                      onClick={leaveAssetEditToList}
                      disabled={loading}
                    >
                      {t('assets.cancelEdit')}
                    </button>
                  </div>
                ) : null}
                {showAssetWireFieldsets ? (
                  <AssetBookingAvailabilitySection
                    id={`${menuId}-booking-availability`}
                    assetBody={assetBody}
                    disabled={loading}
                    onChange={setAssetBody}
                  />
                ) : null}
                <DynamicRecordForm
                  remountKey={`${operation}-${formEpoch}`}
                  data={assetBodyForDynamicForm}
                  onFieldChange={onAssetFieldChange}
                  idPrefix={`${menuId}-asset-${formEpoch}`}
                  generalInfoPicklists={assetFormPicklists}
                  mandatoryMarkerKeys={
                    operation === 'create'
                      ? assetCreateMandatoryMarkers
                      : assetEditing
                        ? assetUpdateMandatoryMarkers
                        : undefined
                  }
                  theme={formTheme}
                  compactLayout={operation === 'create' || assetEditing}
                  readonlyPathPredicate={
                    assetEditing ? isAssetUpdateEditorReadonlyWirePath : undefined
                  }
                />
                {operation === 'create' && canMutateAssets ? (
                  <fieldset
                    className={styles.createAddonFieldset}
                    aria-labelledby={`${menuId}-create-images-legend`}
                  >
                    <legend className={styles.createAddonLegend} id={`${menuId}-create-images-legend`}>
                      {t('assetField.images')}
                    </legend>
                    <p className={styles.createImagesHint}>{t('assets.addUpToTenImages')}</p>
                    <p className={styles.createImagesCount} aria-live="polite">
                      {t('assets.imagesSelected', {
                        n: createImagePaths.length,
                        max: CREATE_ASSET_IMAGES_MAX,
                      })}
                    </p>

                    <div className={styles.createImageSection}>
                      <div className={styles.createImageSectionTitle}>{t('assets.mainImage')}</div>
                      <p className={styles.createImageSectionHint}>{t('assets.mainImageListHint')}</p>
                      <div className={styles.createMainImageRow}>
                        <PickedFileImageThumb
                          key={
                            createImagePreviewSrcs[0]
                              ? `preview-main-${createImagePreviewSrcs[0].slice(0, 48)}`
                              : createMainImagePath ?? 'no-main-image'
                          }
                          wirePath={createMainImagePath}
                          previewSrc={createImagePreviewSrcs[0]}
                          className={styles.createMainImageThumb}
                          wrapClassName={styles.createMainImageThumbWrap}
                        />
                        {createMainImagePath ? (
                          <button
                            type="button"
                            className={styles.createImagesRemove}
                            onClick={() => onRemoveCreateImageAt(0)}
                          >
                            {t('assets.removeImage')}
                          </button>
                        ) : null}
                      </div>
                      {!createMainImagePath ? (
                        <p className={styles.facilityStatus}>{t('assets.noMainImageYet')}</p>
                      ) : null}
                      <div className={styles.createImagesBrowseWrap}>
                        <label className={styles.createImagesBrowseLabel}>
                          <span className={styles.createImagesBrowseSpan}>
                            {t('assets.browseMainImage')}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className={styles.createImagesFileInputOverlay}
                            aria-label={t('assets.browseMainImage')}
                            onChange={onCreateMainImageFileInputChange}
                          />
                        </label>
                      </div>
                    </div>

                    <div className={styles.createImageSection}>
                      <div className={styles.createImageSectionTitle}>
                        {t('assets.additionalImages')}
                      </div>
                      {createAdditionalImagePaths.length > 0 ? (
                        <ul
                          className={styles.createImagesList}
                          aria-label="Additional images selected for upload"
                        >
                          {createAdditionalImagePaths.map((p, idx) => {
                            const imageIndex = idx + 1; // +1 because index 0 is main
                            return (
                              <li key={`${imageIndex}-${p}`} className={styles.createImagesListItem}>
                                <PickedFileImageThumb
                                  wirePath={p}
                                  previewSrc={createImagePreviewSrcs[imageIndex]}
                                  className={styles.updateImagesThumb}
                                  wrapClassName={styles.updateImagesThumbWrap}
                                />
                                <button
                                  type="button"
                                  className={styles.createImagesRemove}
                                  onClick={() => onRemoveCreateImageAt(imageIndex)}
                                >
                                  {t('assets.removeImage')}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className={styles.facilityStatus}>{t('assets.noAdditionalImagesYet')}</p>
                      )}

                      {createImagePaths.length < CREATE_ASSET_IMAGES_MAX ? (
                        <div className={styles.createImagesBrowseWrap}>
                          <label className={styles.createImagesBrowseLabel}>
                            <span className={styles.createImagesBrowseSpan}>
                              {t('assets.browseAdditionalImages')}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className={styles.createImagesFileInputOverlay}
                              aria-label={t('assets.browseAdditionalImages')}
                              onChange={onCreateImagesFileInputChange}
                            />
                          </label>
                        </div>
                      ) : null}
                    </div>
                  </fieldset>
                ) : null}
                {operation === 'getById' && assetEditing && canMutateAssets ? (
                  <fieldset
                    className={styles.createAddonFieldset}
                    aria-labelledby={`${menuId}-update-images-legend`}
                  >
                    <legend className={styles.createAddonLegend} id={`${menuId}-update-images-legend`}>
                      {t('assetField.images')}
                    </legend>
                    <p className={styles.createImagesHint}>{t('assets.addUpToTenImages')}</p>
                    <p className={styles.createImagesCount} aria-live="polite">
                      {t('assets.imagesSelected', {
                        n: updateWireImageUrls.length,
                        max: CREATE_ASSET_IMAGES_MAX,
                      })}
                    </p>

                    <div className={styles.createImageSection}>
                      <div className={styles.createImageSectionTitle}>{t('assets.mainImage')}</div>
                      <p className={styles.createImageSectionHint}>{t('assets.mainImageListHint')}</p>
                      <div className={styles.createMainImageRow}>
                        <PickedFileImageThumb
                          key={
                            updateImagePreviewSrcs[0]
                              ? `update-preview-main-${updateImagePreviewSrcs[0].slice(0, 48)}`
                              : updateMainImagePath ?? 'no-update-main-image'
                          }
                          wirePath={updateMainImagePath}
                          previewSrc={updateImagePreviewSrcs[0]}
                          className={styles.createMainImageThumb}
                          wrapClassName={styles.createMainImageThumbWrap}
                        />
                        {updateMainImagePath ? (
                          <button
                            type="button"
                            className={styles.createImagesRemove}
                            onClick={() => removeUpdateWireImageAt(0)}
                          >
                            {t('assets.removeImage')}
                          </button>
                        ) : null}
                      </div>
                      {!updateMainImagePath ? (
                        <p className={styles.facilityStatus}>{t('assets.noMainImageYet')}</p>
                      ) : null}
                      <div className={styles.createImagesBrowseWrap}>
                        <label className={styles.createImagesBrowseLabel}>
                          <span className={styles.createImagesBrowseSpan}>
                            {t('assets.browseMainImage')}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className={styles.createImagesFileInputOverlay}
                            aria-label={t('assets.browseMainImage')}
                            onChange={onUpdateMainImageFileInputChange}
                          />
                        </label>
                      </div>
                    </div>

                    <div className={styles.createImageSection}>
                      <div className={styles.createImageSectionTitle}>
                        {t('assets.additionalImages')}
                      </div>
                      {updateAdditionalImagePaths.length > 0 ? (
                        <ul
                          className={styles.createImagesList}
                          aria-label="Additional asset images"
                        >
                          {updateAdditionalImagePaths.map((p, idx) => {
                            const imageIndex = idx + 1;
                            return (
                              <li key={`${imageIndex}-${p}`} className={styles.createImagesListItem}>
                                <PickedFileImageThumb
                                  wirePath={p}
                                  previewSrc={updateImagePreviewSrcs[imageIndex]}
                                  className={styles.updateImagesThumb}
                                  wrapClassName={styles.updateImagesThumbWrap}
                                />
                                <button
                                  type="button"
                                  className={styles.createImagesRemove}
                                  onClick={() => removeUpdateWireImageAt(imageIndex)}
                                >
                                  {t('assets.removeImage')}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className={styles.facilityStatus}>{t('assets.noAdditionalImagesYet')}</p>
                      )}

                      {updateWireImageUrls.length < CREATE_ASSET_IMAGES_MAX ? (
                        <div className={styles.createImagesBrowseWrap}>
                          <label className={styles.createImagesBrowseLabel}>
                            <span className={styles.createImagesBrowseSpan}>
                              {t('assets.browseAdditionalImages')}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className={styles.createImagesFileInputOverlay}
                              aria-label={t('assets.browseAdditionalImages')}
                              onChange={onUpdateImagesFileInputChange}
                            />
                          </label>
                        </div>
                      ) : null}
                    </div>
                  </fieldset>
                ) : null}
                {showAssetWireFieldsets ? (
                  <fieldset
                    className={styles.facilityFieldset}
                    aria-labelledby={`${menuId}-create-facilities-legend`}
                  >
                    <legend className={styles.facilityLegend} id={`${menuId}-create-facilities-legend`}>
                      {t('assetField.facilities')}
                    </legend>
                    {createFacilitiesStatus === 'loading' || createFacilitiesStatus === 'idle' ? (
                      <p className={styles.facilityStatus} aria-live="polite">
                        {t('assets.loadingFacilities')}
                      </p>
                    ) : null}
                    {createFacilitiesStatus === 'error' && createFacilitiesError ? (
                      <div className={styles.facilityErrorBlock} role="alert">
                        <p className={styles.facilityErrorText}>{createFacilitiesError}</p>
                        <button
                          type="button"
                          className={styles.facilityRetry}
                          onClick={() => setCreateFacilitiesFetchNonce((n) => n + 1)}
                        >
                          {t('assets.retry')}
                        </button>
                      </div>
                    ) : null}
                    {createFacilitiesStatus === 'ready' && createFacilitiesCatalog.length === 0 ? (
                      <p className={styles.facilityStatus}>{t('assets.noFacilitiesInApi')}</p>
                    ) : null}
                    {createFacilitiesStatus === 'ready' && partitionedCreateFacilities.main.length > 0 ? (
                      <AssetCreateFacilityCheckboxGrid
                        rows={partitionedCreateFacilities.main}
                        menuId={menuId}
                        formEpoch={formEpoch}
                        sectionKey="main"
                        ariaLabel={t('assets.facilitiesAria')}
                        selectedIds={selectedCreateFacilityIds}
                        language={language}
                        t={t}
                        onToggle={onCreateFacilityToggle}
                      />
                    ) : null}
                  </fieldset>
                ) : null}
                {showAssetWireFieldsets ? (
                  <fieldset
                    className={styles.facilityFieldset}
                    aria-labelledby={`${menuId}-create-transport-legend`}
                  >
                    <legend className={styles.facilityLegend} id={`${menuId}-create-transport-legend`}>
                      {t('assets.transportationAccessibility')}
                    </legend>
                    {createFacilitiesStatus === 'loading' || createFacilitiesStatus === 'idle' ? (
                      <p className={styles.facilityStatus} aria-live="polite">
                        {t('assets.loadingFacilities')}
                      </p>
                    ) : null}
                    {createFacilitiesStatus === 'ready' && partitionedCreateFacilities.transportation.length === 0 ? (
                      <p className={styles.facilityStatus}>{t('assets.noTransportationFacilities')}</p>
                    ) : null}
                    {createFacilitiesStatus === 'ready' && partitionedCreateFacilities.transportation.length > 0 ? (
                      <AssetCreateFacilityCheckboxGrid
                        rows={partitionedCreateFacilities.transportation}
                        menuId={menuId}
                        formEpoch={formEpoch}
                        sectionKey="transport"
                        ariaLabel={t('assets.transportationAria')}
                        selectedIds={selectedCreateFacilityIds}
                        language={language}
                        t={t}
                        onToggle={onCreateFacilityToggle}
                      />
                    ) : null}
                  </fieldset>
                ) : null}
                {showAssetWireFieldsets ? (
                  <fieldset
                    className={styles.createAddonFieldset}
                    aria-labelledby={`${menuId}-create-pricing-legend`}
                  >
                    <legend className={styles.createAddonLegend} id={`${menuId}-create-pricing-legend`}>
                      {labelWithCurrencySymbol(t('assets.pricingTypesLegend'), assetFormCurrency)}
                    </legend>
                    {operation === 'create' && createPriceSuggestion ? (
                      <div className={styles.pricingSuggestBanner} role="status">
                        <p className={styles.pricingSuggestText}>
                          {t(
                            createPriceSuggestion.source === 'market'
                              ? 'assets.pricingSuggestMarket'
                              : 'assets.pricingSuggestEstimate',
                            {
                              hourly: `${assetFormCurrency.symbol}${createPriceSuggestion.hourly}`,
                              daily: `${assetFormCurrency.symbol}${createPriceSuggestion.daily}`,
                              monthly: `${assetFormCurrency.symbol}${createPriceSuggestion.monthly}`,
                              n: String(createPriceSuggestion.sampleSize),
                              type: createPricingSuggestionContext.assetTypeLabel ?? t('assets.pricingSuggestAnyType'),
                              city:
                                createPricingSuggestionContext.city ?? t('assets.pricingSuggestAnyCity'),
                            },
                          )}
                        </p>
                        <button
                          type="button"
                          className={styles.pricingSuggestApply}
                          onClick={onApplyCreatePriceSuggestion}
                          disabled={createPricingTypesStatus !== 'ready'}
                        >
                          {t('assets.pricingSuggestApply')}
                        </button>
                      </div>
                    ) : null}
                    {createPricingTypesStatus === 'loading' || createPricingTypesStatus === 'idle' ? (
                      <p className={styles.facilityStatus} aria-live="polite">
                        {t('assets.loadingPricingTypes')}
                      </p>
                    ) : null}
                    {createPricingTypesStatus === 'error' && createPricingTypesError ? (
                      <div className={styles.facilityErrorBlock} role="alert">
                        <p className={styles.facilityErrorText}>{createPricingTypesError}</p>
                        <button
                          type="button"
                          className={styles.facilityRetry}
                          onClick={() => setCreatePricingTypesFetchNonce((n) => n + 1)}
                        >
                          Retry
                        </button>
                      </div>
                    ) : null}
                    {createPricingTypesStatus === 'ready' && createPricingTypesCatalog.length === 0 ? (
                      <p className={styles.facilityStatus}>{t('assets.noPricingTypes')}</p>
                    ) : null}
                    {createPricingTypesStatus === 'ready' && createPricingTypesCatalog.length > 0 ? (
                      <div
                        className={styles.createAddonGrid}
                        role="group"
                        aria-label={t('assets.pricingAria')}
                      >
                        {createPricingTypesCatalog.map((row) => {
                          const pid = pricingTypeRowNumericId(row);
                          if (pid === null) {
                            return null;
                          }
                          const boxId = `${menuId}-pricing-${pid}-${formEpoch}`;
                          const draft = selectedCreatePricingById.get(pid);
                          const checked = draft !== undefined;
                          return (
                            <div
                              key={pid}
                              className={`${styles.createAddonCard}${checked ? ` ${styles.createAddonCardChecked}` : ''}`}
                            >
                              <input
                                id={boxId}
                                type="checkbox"
                                className={styles.createAddonCheckbox}
                                checked={checked}
                                onChange={(ev) => onCreatePricingTypeToggle(row, ev.target.checked)}
                              />
                              <div className={styles.createAddonCardBody}>
                                <label htmlFor={boxId} className={styles.createAddonCardTitle}>
                                  {localizeCatalogRowTitle(row, language, t, 'pricing')}
                                </label>
                                {checked && draft ? (
                                  <div className={styles.createAddonParams}>
                                    <label className={styles.createAddonParam} htmlFor={`${boxId}-usd`}>
                                      <span className={styles.createAddonParamLabel}>
                                        {localizeAssetPriceFieldLabel('price', t, assetFormCurrency)}
                                      </span>
                                      <NonNegativePriceInput
                                        id={`${boxId}-usd`}
                                        className={styles.createAddonParamInput}
                                        value={draft.price}
                                        onChange={(price) => onCreatePricingPriceChange(pid, price)}
                                      />
                                    </label>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </fieldset>
                ) : null}
                {operation === 'create' && canMutateAssets ? (
                  <AssetCreateTermsSection
                    menuId={menuId}
                    open={createTermsOpen}
                    agreed={createTermsAgreed}
                    onToggleOpen={() => setCreateTermsOpen((value) => !value)}
                    onAgreedChange={setCreateTermsAgreed}
                  />
                ) : null}
                {showAssetWireFieldsets ? (
                  <DynamicRecordForm
                    remountKey={`${operation}-${formEpoch}-opening`}
                    data={createOpeningTimesFormData}
                    onFieldChange={onAssetFieldChange}
                    idPrefix={`${menuId}-asset-${formEpoch}-opening`}
                    theme={formTheme}
                    readonlyPathPredicate={
                      assetEditing ? isAssetUpdateEditorReadonlyWirePath : undefined
                    }
                    openingTimesAllowSpecificDates
                  />
                ) : null}
              </div>
            ) : null}

            {hideAssetsFormSubmit ? null : (
            <div className={styles.inputRow} style={{ marginTop: '0.65rem', marginBottom: 0 }}>
              <button
                type={
                  operation === 'getById' && !updateEditReady && showCloseInsteadOfDisplay
                    ? 'button'
                    : 'submit'
                }
                className={styles.submit}
                disabled={
                  loading ||
                  (operation === 'create' &&
                    canMutateAssets &&
                    assetBodyHasInvalidPricingPrices(assetBody))
                }
                onClick={
                  operation === 'getById' && !updateEditReady && showCloseInsteadOfDisplay
                    ? (ev) => {
                        ev.preventDefault();
                        onCloseDisplayedView();
                      }
                    : undefined
                }
              >
                {submitLabel(operation, loading, updateEditReady, showCloseInsteadOfDisplay, t)}
              </button>
              {operation === 'getById' && assetEditing ? (
                <button
                  type="button"
                  className={styles.lastUsedUpdateIdUseButton}
                  onClick={leaveAssetEditToList}
                  disabled={loading}
                >
                  {t('assets.cancelEdit')}
                </button>
              ) : null}
            </div>
            )}
          </form>
          {error instanceof AssetRequestError ? (
            <AssetRequestErrorPanel error={error} />
          ) : error ? (
            <p
              ref={panelErrorNoticeRef}
              tabIndex={-1}
              className={isBrowsePage ? pageThemeStyles.browseError : styles.error}
              role="alert"
            >
              {error}
            </p>
          ) : clientBrowseActive && clientBrowseSearch.error ? (
            <p
              ref={panelErrorNoticeRef}
              tabIndex={-1}
              className={isBrowsePage ? pageThemeStyles.browseError : styles.error}
              role="alert"
            >
              {clientBrowseSearch.error}
            </p>
          ) : null}
          {assetEditing ? null : response || clientBrowseActive ? (
            <>
              {response?.operation === 'getById' &&
              displayedGetByIdAsset &&
              canRenderAssetGetByIdView(displayedGetByIdAsset) &&
              !assetEditing ? (
                <>
                  {updateSuccessBanner}
                  <AssetGetByIdView
                  data={displayedGetByIdAsset}
                  theme={formTheme}
                  layout={useAssetProfileLayout ? 'profile' : 'detail'}
                  ownerLogoOptions={assetOwnerLogoOptions}
                  onBookAsset={
                    showBookFromActiveList &&
                    isPlainObject(response.data) &&
                    !showInlineAssetQuickBook
                      ? () => onBookAssetFromDetail(response.data as Record<string, unknown>)
                      : undefined
                  }
                  bookDisabled={bookButtonsDisabled}
                  bookDisabledTitle={bookDisabledTitle}
                />
                {ownerScheduleAssetId ? (
                  <AssetOwnerSchedulePanel
                    assetId={ownerScheduleAssetId}
                    assetRecord={ownerScheduleAssetRecord}
                    language={language}
                    theme={formTheme}
                  />
                ) : null}
                {showInlineAssetQuickBook && inlineQuickBookAsset ? (
                  <AssetQuickBookingPanel
                    asset={inlineQuickBookAsset}
                    currency={inlineQuickBookCurrency}
                    onProceed={onInlineAssetQuickBookProceed}
                    onCancel={onInlineAssetQuickBookCancel}
                    disabled={bookButtonsDisabled}
                    disabledTitle={bookDisabledTitle}
                    initialDayKey={assistantBookingDayKey}
                    initialStartTime={assistantBookingStartTime}
                  />
                ) : null}
                </>
              ) : displayedListDetailAsset ? (
                <>
                  {canMutateAssets && !ownerCatalogBrowse ? (
                    <div className={isBrowsePage ? pageThemeStyles.browseToolbar : styles.listDetailToolbar}>
                      <button
                        type="button"
                        className={styles.lastUsedUpdateIdUseButton}
                        onClick={() => void onBeginUpdateFromListDetail()}
                        disabled={loading || !listDetailUpdateOwnerOk}
                        title={
                          listDetailUpdateOwnerOk
                            ? undefined
                            : 'Only the listed owner can edit this asset with your signed-in account.'
                        }
                      >
                        {t('assets.updateAsset')}
                      </button>
                    </div>
                  ) : null}
                  {updateSuccessBanner}
                  <AssetGetByIdView
                    data={displayedListDetailAsset}
                    theme={formTheme}
                    layout={useAssetProfileLayout ? 'profile' : 'detail'}
                    ownerLogoOptions={assetOwnerLogoOptions}
                    onBookAsset={
                      showBookFromActiveList && !showInlineAssetQuickBook && displayedListDetailAsset
                        ? () => onBookAssetFromDetail(displayedListDetailAsset)
                        : undefined
                    }
                    bookDisabled={bookButtonsDisabled}
                    bookDisabledTitle={bookDisabledTitle}
                  />
                  {ownerScheduleAssetId ? (
                    <AssetOwnerSchedulePanel
                      assetId={ownerScheduleAssetId}
                      assetRecord={ownerScheduleAssetRecord}
                      language={language}
                      theme={formTheme}
                    />
                  ) : null}
                  {showInlineAssetQuickBook && inlineQuickBookAsset ? (
                    <AssetQuickBookingPanel
                      asset={inlineQuickBookAsset}
                      currency={inlineQuickBookCurrency}
                      onProceed={onInlineAssetQuickBookProceed}
                      onCancel={onInlineAssetQuickBookCancel}
                      disabled={bookButtonsDisabled}
                      disabledTitle={bookDisabledTitle}
                      initialDayKey={assistantBookingDayKey}
                      initialStartTime={assistantBookingStartTime}
                    />
                  ) : null}
                </>
              ) : openingDeepLinkedAsset ? (
                <p className={isBrowsePage ? pageThemeStyles.browseEmpty : styles.emptyHint} role="status">
                  {t('common.loading')}
                </p>
              ) : clientBrowseActive ||
                (response &&
                  isListLikePanelOperation(response.operation) &&
                  listRecordsBase.length > 0) ? (
                <div className={isBrowsePage ? pageThemeStyles.browseListWrap : undefined}>
                  {clientBrowseActive ? (
                    <BookableAssetSearchResults
                      records={displayListRecords}
                      total={clientBrowseListView?.total ?? clientBrowseSearch.total}
                      loading={clientBrowseSearch.loading}
                      loadingMore={clientBrowseSearch.loadingMore}
                      catalogExpanding={clientBrowseSearch.catalogExpanding}
                      hasMore={clientBrowseSearch.hasMore}
                      searchMode={clientBrowseSearch.searchMode}
                      onSelect={onSelectListAsset}
                      onLoadMore={clientBrowseSearch.loadMore}
                      showBook={showBookFromActiveList}
                      onBook={onBookFromList}
                      bookDisabled={bookButtonsDisabled}
                      bookDisabledReason={bookDisabledTitle}
                      theme={isBrowsePage ? 'light' : 'dark'}
                      emptyMessage={
                        assetsBrowseParams.open24h ? open24hEmptyMessage : t('assets.filter.noMatch')
                      }
                      emptyActionLabel={assetsBrowseParams.open24h ? open24hEmptyActionLabel : undefined}
                      onEmptyAction={assetsBrowseParams.open24h ? onSearchAllOfficesInCity : undefined}
                      hideCount={isBrowsePage}
                    />
                  ) : displayListRecords.length === 0 ? (
                    <div
                      className={isBrowsePage ? pageThemeStyles.browseNoMatch : styles.assetListNoMatch}
                      role="status"
                    >
                      <p>
                        {listRecordsBase.length === 0
                          ? 'No assets in the response.'
                          : assetsBrowseParams.open24h
                            ? open24hEmptyMessage
                            : t('assets.filter.noMatch')}
                      </p>
                      {listRecordsBase.length > 0 && assetsBrowseParams.open24h ? (
                        <button
                          type="button"
                          className={
                            isBrowsePage ? pageThemeStyles.browseBackButton : styles.lastUsedUpdateIdUseButton
                          }
                          onClick={onSearchAllOfficesInCity}
                        >
                          {open24hEmptyActionLabel}
                        </button>
                      ) : listRecordsBase.length > 0 ? (
                        <button
                          type="button"
                          className={
                            isBrowsePage ? pageThemeStyles.browseBackButton : styles.lastUsedUpdateIdUseButton
                          }
                          onClick={onClearListFilters}
                        >
                          {t('assets.filter.clearFilters')}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <AssetListView
                      assets={displayListRecords}
                      onSelect={onSelectListAsset}
                      layout={ownerAssetListGridOnBrowse ? 'responsiveGrid' : 'rows'}
                      theme={isBrowsePage ? 'light' : 'dark'}
                      showBook={showBookFromActiveList}
                      onBook={onBookFromList}
                      bookDisabled={bookButtonsDisabled}
                      bookDisabledReason={bookDisabledTitle}
                      showFavoriteHeart={response?.operation !== 'listMine'}
                      showEdit={canMutateAssets && response?.operation === 'listMine'}
                      onEdit={onEditAssetFromList}
                      canEditAsset={(a: Record<string, unknown>) =>
                        canMutateAssetAsSessionOwner(a, getSessionToken())
                      }
                      editingAssetId={listEditBusyId}
                      showBookingAvailability={canMutateAssets && response?.operation === 'listMine'}
                      onToggleBookingAvailability={onToggleBookingAvailabilityFromList}
                      bookingAvailabilityBusyId={listAvailabilityBusyId}
                      canToggleBookingAvailability={(a: Record<string, unknown>) =>
                        canMutateAssetAsSessionOwner(a, getSessionToken())
                      }
                      showDelete={canMutateAssets}
                      canDeleteAsset={(a: Record<string, unknown>) =>
                        canMutateAssetAsSessionOwner(a, getSessionToken())
                      }
                      deletingAssetId={listDeleteBusyId}
                      onDelete={onDeleteAssetFromList}
                    />
                  )}
                </div>
              ) : response && response.operation === 'create' ? null : response && response.operation === 'delete' ? (
                <p className={styles.deleteSuccess} role="status">
                  {response.data === null || response.data === undefined
                    ? 'The asset doesn\'t exist'
                    : 'The asset was deleted.'}
                </p>
              ) : response &&
                isListLikePanelOperation(response.operation) &&
                isExplicitlyEmptyAssetList(response.data) ? (
                <p className={isBrowsePage ? pageThemeStyles.browseEmpty : styles.emptyList}>
                  {canMutateAssets && response.operation === 'listMine'
                    ? t('assets.ownerEmpty')
                    : 'No assets in the response.'}
                </p>
              ) : response ? (
                <details className={styles.responseRawDetails}>
                  <summary>Raw JSON</summary>
                  <pre className={styles.pre}>{JSON.stringify(response.data, null, 2)}</pre>
                </details>
              ) : null}
            </>
          ) : !error && !loading && panelEmptyHint ? (
            <p className={isBrowsePage ? pageThemeStyles.browseEmpty : styles.emptyHint}>{panelEmptyHint}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
